import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useGridCopyMenu } from './gridCommon';
import DateInput from './DateInput';
import { AgGridReact } from 'ag-grid-react';
import {
  AllCommunityModule,
  ClientSideRowModelModule,
  type ColDef,
  ModuleRegistry,
} from "ag-grid-community";
import "ag-grid-community/styles/ag-theme-quartz.css";
import { type PfMst, type PfOrder, type PfPos, type PfBs, type KisPfFill, type KisPfSlice, type KisApiMst } from '../types';
import { callAxiosGet, callAxios } from '../tmslib/src/util/axiosutils';
import { useMessageState } from '../tmslib/src/context/MessageContext';
import { getMainAxios } from '../utils/util';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { getApiConfig } from '../config/api';
import * as signalR from '@microsoft/signalr';
import MarketAtDialog from './MarketAtDialog';
import StockChart, { TradeMarker } from './StockChart';
import { signColor as pnlColor } from '../styles/fdsTheme';

ModuleRegistry.registerModules([
  AllCommunityModule,
  ClientSideRowModelModule,
]);

// 간단한 toast 함수 구현
const toast = (message: string) => {
  console.log(`Toast: ${message}`);
  alert(message);
};

interface AddOrderParams {
  d: string;
  pfId: number;
  prodId: string;
  ls: number;
  ex: number;
  wei: number;
  qty?: number | null;
  exitAll: boolean;
  limitPrc?: number | null;
  limitIdx?: number | null;
  stopPrc?: number | null;
  hm0: string;
  hm1?: string | null;
  nSlices?: number | null;
  msg?: string | null;
  pfStratId?: number | null; // 주문 슬롯 (null = 계좌의 MANUAL)
}

interface ModifyOrderParams {
  ordId: number;
  wei: number;
  qty?: number | null;
  exitAll: boolean;
  limitPrc?: number | null;
  limitIdx?: number | null;
  stopPrc?: number | null;
  hm0: string;
  hm1?: string | null;
  nSlices?: number | null;
  msg?: string | null;
}

interface CancelOrderParams {
  ordId: number;
}

// 증권사 코드 → 표시명
// 컨텍스트 메뉴 구획선 — 기능 그룹을 눈에 띄게 나눈다 (진한 선 + 여백)
const menuSepStyle: React.CSSProperties = {
  borderTop: '2px solid #cbd5e1',
  margin: '5px 0',
};
// 구획 라벨 — 어떤 그룹인지 알려준다
const menuLabelStyle: React.CSSProperties = {
  padding: '3px 12px',
  fontSize: '11px',
  fontWeight: 700,
  color: '#94a3b8',
  letterSpacing: '0.04em',
};

const brokerNm = (b?: string) =>
  b === 'kiwoom' ? '키움' : b === 'kis' ? '한투' : (b || '-');

// 계좌 표시: id 이름 / 증권사 / 시작일자 / 계좌번호
const pfLabel = (p: KisApiMst) =>
  `${p.id} ${p.idNm || p.htsId || '-'} / ${brokerNm(p.broker)} / ${p.initD || '-'} / ${p.accountNo}`;

/**
 * 일별 손익 집계 1행 (main.kispfpnl) — fdscronjob 의 GenPfBSJob 이 5분마다 채운다.
 *
 * 바로 위 KIS 잔고 그리드와 **대조**하라고 두는 표다:
 *   KIS 잔고   : 순자산·일손익 등 KIS 가 내려준 값
 *   손익(pfbs) : 체결(kispffill)에서 **우리가 직접** 계산한 값 + KIS 값 + 그 차이
 *
 * KIS 모의투자가 일손익(asst_icdc_amt)을 0 으로 주던 사고(2026-10-07) 때문에
 * KIS 값 하나만 보지 않는다. 화면은 읽기만 하고 계산은 서버(GenPfBSJob)가 한다.
 */
/**
 * 체결손익 세부 — **종목별**로 어떻게 그 숫자가 나왔는지 (daily-detail API).
 *
 * 손익 집계의 '체결손익' 은 종목별 (매도대금 − 매수대금) 의 합이다.
 * 합계만 보면 어느 종목이 벌고 잃었는지 모르니 펼쳐서 보여준다.
 */
interface TradePnlRow {
  prodId: string; prodNm?: string;
  bQty: number; sQty: number; bAmt: number; sAmt: number;
  bAvg: number; sAvg: number; pnl: number; pnlRt: number; flat: boolean;
}

interface PnlRow {
  htsId: string; id: number; pfId: number; d: string;
  realized: number; buyAmt: number; sellAmt: number;
  buyQty: number; sellQty: number; nFill: number; nSym: number;
  fee: number; tax: number; cost: number;
  net: number; unrealized: number; total: number;
  nav?: number | null; prevNav?: number | null;
  navDiff?: number | null; diffCheck?: number | null;
  // KIS 가 직접 준 일손익(asst_icdc_amt)과 그 차이 — 검증의 기준값
  kisDiffAmt?: number | null; kisGap?: number | null;
  flat: boolean; retPct?: number | null; updAt?: string | null;
}

const PortfolioOverview: React.FC = () => {
  const copyMenu = useGridCopyMenu();
  const { selectedPfId, portfolios, selectPortfolio, isDev } = useAuth();
  // 선택 전이면 목록 첫 계좌(=사용중·기간 안·최신, AuthContext.sortPortfolios). 예전엔 1 로 고정해 정지된 계좌가 떴다 (2026-11-03)
  const portfolioId = selectedPfId ?? portfolios[0]?.id ?? 0;
  // 이 계좌에 주문할 수 있나 (서버 PfAccess) — 없으면 주문 버튼·메뉴를 숨긴다. 실제 차단은 서버가 한다 (2026-10-27)
  const pfCanOrder = useMemo(
    () => portfolioId !== 0 && portfolios.find(p => p.id === portfolioId)?.canOrder !== false,
    [portfolios, portfolioId],
  );
  // 살아있는(사용중) 계좌만 노출 — 정지 계좌는 '포트폴리오복기'에서 본다
  const activePfs = useMemo(
    () => portfolios.filter(p => p.isActive !== false),
    [portfolios],
  );
  // 선택 계좌가 가상체결거래소(broker='sim')인지 — SIM 체결 버튼 노출 조건
  const isSimAccount = useMemo(
    () => portfolios.find(p => p.id === portfolioId)?.broker === 'sim',
    [portfolios, portfolioId],
  );
  // 선택 계좌의 초기투자금액 (kisapimst.initAmt) — 누적손익·수익률 계산 기준
  const selectedPfInitAmt = useMemo(
    () => portfolios.find(p => p.id === portfolioId)?.initAmt ?? 0,
    [portfolios, portfolioId],
  );
  // 계좌별 초기투자금 맵 — 잔고 그리드는 "전체" 선택 시 여러 계좌 행을 함께 보여준다.
  // 행마다 pfId 가 다르므로 선택 계좌 값 하나로 계산하면 틀린다.
  // (2026-08-29: simtest(pfid=7, 1억) 행에 pfid=6 의 1천만원이 찍혀
  //  누적손익 9천만·수익률 900% 로 나왔다)
  const initAmtByPfId = useMemo(() => {
    const m = new Map<number, number>();
    portfolios.forEach(p => m.set(p.id, p.initAmt ?? 0));
    return m;
  }, [portfolios]);
  // 행의 계좌 기준. pfId 가 없으면 선택 계좌로 폴백한다.
  const initAmtOf = useCallback(
    (row: any): number =>
      (row?.pfId != null ? initAmtByPfId.get(row.pfId) : undefined) ?? selectedPfInitAmt,
    [initAmtByPfId, selectedPfInitAmt],
  );
  // pfId=0이면 "전체" → API에 pfId를 보내지 않음
  const apiPfId = portfolioId === 0 ? undefined : portfolioId;
  // 잔고도 선택 계좌를 따른다 (전체 선택 시에만 undefined → 전 계좌 조회)
  const balancePfId = apiPfId;
  
  const [orders, setOrders] = useState<PfOrder[]>([]);
  const [positions, setPositions] = useState<PfPos[]>([]);
  const [recentBalance, setRecentBalance] = useState<PfBs[]>([]);
  // 손익 집계(main.kispfpnl). 위 KIS 잔고 그리드와 대조용 — 화면은 읽기만 한다.
  const [pnlRows, setPnlRows] = useState<PnlRow[]>([]);
  // 체결손익 세부(종목별). 손익 집계 행을 클릭하면 펼친다.
  const [tradeDetail, setTradeDetail] = useState<TradePnlRow[] | null>(null);
  const [tradeDetailD, setTradeDetailD] = useState<string | null>(null);
  const [fills, setFills] = useState<KisPfFill[]>([]);
  const fillsRef = useRef<KisPfFill[]>([]);
  fillsRef.current = fills;
  const [loading, setLoading] = useState(true);
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; fill: KisPfFill; cellValue?: string; rowText?: string } | null>(null);
  // 체결 시점 시장상황 팝업 (주문가격 vs 실제 호가·체결)
  const [marketAt, setMarketAt] = useState<{ prodId: string; prodNm?: string; d: string; tm: string; fillPrc?: number } | null>(null);
  const [orderContextMenu, setOrderContextMenu] = useState<{ x: number; y: number; order: PfOrder; cellValue?: string; rowText?: string } | null>(null);
  const [posContextMenu, setPosContextMenu] = useState<{ x: number; y: number; pos: PfPos } | null>(null);

  // AG Grid 행에서 셀/행 텍스트를 추출하는 헬퍼
  const getAgGridCellInfo = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    const cell = target.closest('.ag-cell') as HTMLElement;
    const row = target.closest('.ag-row') as HTMLElement;
    const cellValue = cell?.textContent?.trim() || '';
    const rowText = row ? Array.from(row.querySelectorAll('.ag-cell')).map(c => c.textContent?.trim() || '').join('\t') : '';
    return { cellValue, rowText };
  };

  // HTTP에서도 동작하는 클립보드 복사
  const copyToClipboard = (text: string) => {
    if (navigator.clipboard && window.isSecureContext) {
      // 주의: 예전에 여기가 copyToClipboard(text) 자기호출이라 무한재귀로
      //       HTTPS/localhost 에서 복사가 아예 동작하지 않았다 (2026-08-19 수정).
      navigator.clipboard.writeText(text);
    } else {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
  };
  /**
   * 주문내역 그리드 전체를 탭 구분자로 직렬화 (엑셀 붙여넣기용).
   * 주문 그리드는 정정/취소 커스텀 메뉴 때문에 suppressContextMenu={true} 라
   * gridCommon 의 표준 "전체 복사"를 못 쓴다. 그래서 여기서 따로 만든다.
   * columnDefs 의 valueFormatter 를 적용해 화면 표시값과 동일하게 맞춘다.
   */
  const serializeOrders = (withHeader: boolean): string => {
    const cols = orderColumnDefs.filter(c => (c as any).field);
    const lines: string[] = [];
    if (withHeader) {
      lines.push(cols.map(c => (c as any).headerName ?? (c as any).field).join('\t'));
    }
    for (const row of orders) {
      lines.push(cols.map(c => {
        const field = (c as any).field as string;
        const raw = (row as any)[field];
        const fmt = (c as any).valueFormatter;
        let v: any = raw;
        if (typeof fmt === 'function') {
          try { v = fmt({ value: raw, data: row } as any); } catch { v = raw; }
        }
        return v == null ? '' : String(v).replace(/[\t\n\r]/g, ' ');
      }).join('\t'));
    }
    return lines.join('\n');
  };

  const [error, setError] = useState<string | null>(null);
  // 긴 메시지(에러메시지·코멘트) 전문 보기. 셀을 클릭하면 채워진다.
  const [msgDetail, setMsgDetail] = useState<{ title: string; text: string } | null>(null);
  // 분봉 차트에 올릴 종목. 주문/체결 행의 종목코드를 클릭하면 설정된다.
  const [chartStock, setChartStock] = useState<{ prodId: string; prodNm: string } | null>(null);
  // 사용자가 종목을 직접 골랐나. true 면 자동선택이 손대지 않는다.
  // 조회일자·계좌가 바뀌면 false 로 돌아간다 — 새 날짜엔 그 종목이 없을 수 있다.
  const [userPickedStock, setUserPickedStock] = useState(false);
  const fillGridRef = useRef<HTMLDivElement>(null);
  const orderGridRef = useRef<HTMLDivElement>(null);
  const [showModifyModal, setShowModifyModal] = useState(false);
  const [modifyFill, setModifyFill] = useState<KisPfFill | null>(null);
  const [modifyPrice, setModifyPrice] = useState<string>('');
  const [modifyQty, setModifyQty] = useState<string>('');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedOrder, setSelectedOrder] = useState<PfOrder | null>(null);
  const [showOrderForm, setShowOrderForm] = useState(false);
  const [slices, setSlices] = useState<KisPfSlice[]>([]);
  const [sliceOrderId, setSliceOrderId] = useState<number | null>(null);
  const [filteredFills, setFilteredFills] = useState<KisPfFill[]>([]);
  // 전략 슬롯 필터 — '' = 전체, 그 외 = slotId (주문·체결 그리드 공통)
  const [slotFilter, setSlotFilter] = useState<string>('');
  const [fillsFiltered, setFillsFiltered] = useState(false);
  const [nonBizDays, setNonBizDays] = useState<string[]>([]);
  const { msgBox: m, logger } = useMessageState();
  const axiosInstance = getMainAxios();

  // KIS API 를 거치는 갱신(포지션·체결)은 10초로는 모자란다.
  //   실측 2026-10-03: update-fill(9/25~10/02) 11.6초 → 기본 timeout(10초)에서
  //   "timeout of 10000ms exceeded" 로 끊겼다. 서버는 정상 처리(200, 150건)인데
  //   화면만 실패로 보였다.
  // KIS 왕복 + 페이징이라 기간이 길수록 더 걸린다. 이 두 호출만 120초로 늘린다
  // (전역 TIMEOUT 을 올리면 진짜 죽은 요청까지 오래 매달린다).
  // 공유 인스턴스를 복제하지 않는다 — axios 인스턴스는 호출 가능한 함수라
  // 스프레드로 복사하면 깨진다. timeout 만 바꾼 별도 인스턴스를 만든다.
  const slowAxios = useMemo(() => {
    const base = getMainAxios();
    const inst = axios.create({
      baseURL: base.defaults.baseURL,
      timeout: 120_000,
      headers: base.defaults.headers?.common,
    });
    // 토큰은 요청 시점에 붙인다 (공유 인스턴스의 인터셉터와 동일 동작)
    inst.interceptors.request.use((cfg) => {
      const token = localStorage.getItem('fds_token');
      if (token) cfg.headers.Authorization = `Bearer ${token}`;
      return cfg;
    });
    return inst;
  }, []);
  
  // 주문 폼 상태
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    prodId: '',
    prodNm: '',
    isBuy: true,
    wei: 0,
    qty: null as number | null,
    msg: '' as string,
    exitAll: false,
    prcTy: 'Opp' as 'Opp' | 'My' | 'Limit' | 'Stop',
    oppLmtIdx: 1,
    myLmtIdx: 1,
    limitPrc: null as number | null,
    stopPrc: null as number | null,
    isSlice: false,
    hm0: '09:00',
    hm1: '15:20',
    nSlices: null as number | null
  });
  const [errors, setErrors] = useState<{[key: string]: string}>({});

  // 종목 검색 상태
  const [stockSearchTerm, setStockSearchTerm] = useState('');
  const [stockSearchResults, setStockSearchResults] = useState<Array<{prodId: string, prodNm: string, market: string}>>([]);
  const [showStockSearch, setShowStockSearch] = useState(false);

  // 종목 검색 디바운싱
  useEffect(() => {
    const timer = setTimeout(() => {
      if (stockSearchTerm && stockSearchTerm.trim().length >= 2) {
        handleStockSearch(stockSearchTerm);
      } else {
        setStockSearchResults([]);
        setShowStockSearch(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [stockSearchTerm]);

  const handleStockSearch = async (term: string) => {
    try {
      const response = await axiosInstance.get('/api/v1/stocks/search', {
        params: { term: term.trim(), limit: 20 }
      });
      if (response.data?.success && response.data?.data) {
        setStockSearchResults(response.data.data);
        setShowStockSearch(true);
      } else {
        setStockSearchResults([]);
        setShowStockSearch(true);
      }
    } catch (error: any) {
      console.error('[포트폴리오] 종목 검색 실패:', error);
      setStockSearchResults([]);
      setShowStockSearch(false);
    }
  };

  const handleSelectStock = (stock: {prodId: string, prodNm: string}) => {
    setFormData(prev => ({ ...prev, prodId: stock.prodId, prodNm: stock.prodNm }));
    setStockSearchTerm('');
    setStockSearchResults([]);
    setShowStockSearch(false);
  };

  // 주문 폼 열릴 때 검색 상태 초기화
  useEffect(() => {
    if (showOrderForm) {
      setStockSearchTerm('');
      setStockSearchResults([]);
      setShowStockSearch(false);
    }
  }, [showOrderForm]);

  // 주문 슬롯 — 사용자 → 계좌 → 슬롯 (2026-10-27).
  // 슬롯 단위 권한(orderSlots)이면 그 슬롯 중에서 반드시 고른다. 계좌 전체 권한이면 '기본(MANUAL)' + 운용 중 슬롯.
  const pfOrderSlots = useMemo(
    () => portfolios.find(p => p.id === portfolioId)?.orderSlots ?? null,
    [portfolios, portfolioId],
  );
  const [slotOpts, setSlotOpts] = useState<{ id: number; name: string }[]>([]);
  const [orderSlotId, setOrderSlotId] = useState<number | null>(null);
  useEffect(() => {
    if (!showOrderForm || selectedOrder || !pfCanOrder) return;
    axiosInstance.get('/api/v1/pfstrat/slots', { params: { pfId: portfolioId } })
      .then(res => {
        const all = ((res.data?.slots ?? []) as { id: number; name: string; enabled: boolean; isSystem: boolean }[])
          .filter(s => s.enabled && !s.isSystem && (!pfOrderSlots || pfOrderSlots.includes(s.id)));
        setSlotOpts(all.map(s => ({ id: s.id, name: s.name })));
        setOrderSlotId(pfOrderSlots ? (all[0]?.id ?? null) : null);
      })
      .catch(() => setSlotOpts([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showOrderForm, selectedOrder, portfolioId, pfOrderSlots, pfCanOrder]);

  // Orders 데이터 로드
  const fetchOrders = (pfId: number | undefined, d0: string, d1: string) => {
    callAxiosGet({
      axiosInstance,
      m,
      logger,
      url: '/api/v1/portfolio/orders',
      params: { id: pfId, d0, d1 },
      onSuccess: (data) => {
        console.log('✅ Portfolio Orders API 응답:', data);
        setOrders(data as PfOrder[] || []);
      },
      onError: () => {
        console.log('⚠️ Orders API 연결 실패');
        setOrders([]);
      },
    });
  };

  // Positions 데이터 로드
  const fetchPositions = (pfId: number | undefined, d: string) => {
    callAxiosGet({
      axiosInstance,
      m,
      logger,
      url: '/api/v1/portfolio/positions',
      params: { id: pfId, d },
      onSuccess: (data) => {
        console.log('✅ Portfolio Positions API 응답:', data);
        console.log('✅ 포지션 데이터 개수:', (data as PfPos[])?.length || 0);
        if (data && (data as PfPos[]).length > 0) {
          console.log('✅ 첫 번째 포지션 데이터 샘플:', (data as PfPos[])[0]);
        }
        setPositions((data as PfPos[]) || []);
      },
      onError: () => {
        console.log('⚠️ Positions API 연결 실패');
        setPositions([]);
      },
    });
  };

  // 최근일자 잔고 조회
  const fetchRecentBalance = (pfId: number | undefined, d: string) => {
    callAxiosGet({
      axiosInstance,
      m,
      logger,
      url: '/api/v1/pfbs/recent',
      params: { pfId, d },
      onSuccess: (data) => {
        console.log('✅ Recent Balance API 응답:', data);
        if (Array.isArray(data)) {
          const sorted = (data as PfBs[]).sort((a, b) => (a.pfId ?? 0) - (b.pfId ?? 0));
          setRecentBalance(sorted);
        } else {
          setRecentBalance([]);
        }
      },
      onError: () => {
        console.log('⚠️ Recent Balance API 연결 실패');
        setRecentBalance([]);
      },
    });
  };

  /**
   * 손익 집계 조회 — **조회일자 하루만** 본다.
   * 위 KIS 잔고 그리드가 그날 한 행이라, 같은 날 한 행끼리 나란히 놓고 비교한다.
   * (여러 날 추이는 포트폴리오복기 쪽에서 본다)
   */
  const fetchPnl = (pfId: number | undefined, d?: string) => {
    const day = d || new Date().toISOString().split('T')[0];
    callAxiosGet({
      axiosInstance, m, logger,
      url: '/api/v1/pfbs/pnl',
      params: { pfId, startDate: day, endDate: day, limit: 20 },
      onSuccess: (data) => setPnlRows(Array.isArray(data) ? data as PnlRow[] : []),
      onError: () => setPnlRows([]),
    });
  };

  /**
   * 체결손익 세부(종목별) 조회 — 손익 집계 행 클릭 시.
   *
   * 이미 있는 daily-detail 을 그대로 쓴다. 같은 계산(매도대금−매수대금)을
   * 종목 단위로 하는 엔드포인트가 이미 있어 새로 만들지 않았다 —
   * 두 벌이 되면 합계와 세부가 어긋날 수 있다.
   */
  const fetchTradeDetail = (pfId: number | undefined, d: string) => {
    if (pfId == null) return;
    callAxiosGet({
      axiosInstance, m, logger,
      url: '/api/v1/portfolio/daily-detail',
      params: { id: pfId, d },
      onSuccess: (data) => {
        const rows = (data as any)?.tradePnl;
        setTradeDetail(Array.isArray(rows) ? rows as TradePnlRow[] : []);
        setTradeDetailD(d);
      },
      onError: () => { setTradeDetail([]); setTradeDetailD(d); },
    });
  };

  // 체결 내역 조회 (조회일자 기준)
  const fetchFills = (pfId: number | undefined, startDate?: string, endDate?: string, clearFilter?: boolean) => {
    const today = new Date().toISOString().split('T')[0];
    const start = startDate || today;
    const end = endDate || today;

    callAxiosGet({
      axiosInstance,
      m,
      logger,
      url: '/api/v1/pfbs/fills',
      params: { pfId, startDate: start, endDate: end },
      onSuccess: (data) => {
        console.log('✅ Fills API 응답:', data);
        const fillData = Array.isArray(data) ? data as KisPfFill[] : [];
        // 주문시각 오름차순 정렬 (시간순)
        fillData.sort((a: any, b: any) => (a.ordT || '').localeCompare(b.ordT || ''));
        setFills(fillData);
        if (clearFilter) {
          setFillsFiltered(false);
          setFilteredFills([]);
        }
      },
      onError: () => {
        console.log('⚠️ Fills API 연결 실패');
        setFills([]);
      },
    });
  };

  // 슬라이스 조회 + 체결 필터링
  const fetchSlices = async (ordId: number) => {
    try {
      // 슬라이스 조회
      const sliceRes = await axiosInstance.get('/api/v1/pfbs/slices', { params: { ordId } });
      const sliceData = Array.isArray(sliceRes.data) ? sliceRes.data as KisPfSlice[] : [];
      console.log('✅ Slices API 응답:', sliceData.length, '건');
      setSlices(sliceData);
      setSliceOrderId(ordId);

      // 슬라이스 ordNo로 체결내역 필터링 (fills가 비어있으면 API 재조회)
      const ordNos = new Set(sliceData.map(s => s.ordNo).filter((n): n is number => n != null && n > 0));
      if (ordNos.size > 0) {
        let currentFills = fillsRef.current;
        if (currentFills.length === 0) {
          // fills가 비어있으면 다시 조회
          const fillRes = await axiosInstance.get('/api/v1/pfbs/fills', { params: { pfId: apiPfId, startDate: selectedDate, endDate: selectedDate } });
          currentFills = Array.isArray(fillRes.data) ? fillRes.data : [];
          currentFills.sort((a: any, b: any) => (a.ordT || '').localeCompare(b.ordT || ''));
          setFills(currentFills);
        }
        const filtered = currentFills.filter(f => ordNos.has(f.ordNo));
        filtered.sort((a: any, b: any) => (a.ordT || '').localeCompare(b.ordT || ''));
        setFilteredFills(filtered);
        setFillsFiltered(true);
      } else {
        setFilteredFills([]);
        setFillsFiltered(true);
      }
    } catch {
      console.log('⚠️ Slices API 연결 실패');
      setSlices([]);
      setSliceOrderId(ordId);
      setFilteredFills([]);
      setFillsFiltered(true);
    }
  };

  // 체결 내역 주문 취소
  const handleCancelOrder = async (fill: KisPfFill) => {
    // 잔여수량이 0이 아닌 경우에만 취소 가능 (테스트를 위해 주석 처리)
    // if (!fill.rmnQty || fill.rmnQty === 0) {
    //   alert('취소할 수량이 없습니다.');
    //   return;
    // }

    if (!confirm(`주문을 취소하시겠습니까?\n종목: ${fill.prodNm}\n주문번호: ${fill.ordNo}\n잔여수량: ${(fill.rmnQty || fill.ordQty)?.toLocaleString()}주`)) {
      return;
    }

    try {
      const response = await axiosInstance.post('/api/v1/pfbs/fill-cancel', {
        ordNo: fill.ordNo,
        orgOrdNo: fill.orgOrdNo,
        prodId: fill.prodId,
        ls: fill.ls,
        lsNm: fill.lsNm,
        qty: fill.rmnQty || fill.ordQty,
        ordQty: fill.ordQty,
        prc: fill.ordPrc,
        ordPrc: fill.ordPrc,
      });

      if (response.data.success) {
        alert('주문 취소가 완료되었습니다.');
        // 데이터 새로고침
        fetchFills(apiPfId, selectedDate, selectedDate);
        fetchPnl(apiPfId, selectedDate);
        fetchOrders(apiPfId, selectedDate, selectedDate);
      } else {
        alert(`주문 취소 실패: ${response.data.error || '알 수 없는 오류'}`);
      }
    } catch (error: any) {
      console.error('주문 취소 에러:', error);
      alert(`주문 취소 실패: ${error.response?.data?.error || error.message || '알 수 없는 오류'}`);
    }
  };

  // 체결 내역 주문 정정 모달 열기
  const handleModifyOrder = (fill: KisPfFill) => {
    setModifyFill(fill);
    setModifyPrice(fill.ordPrc?.toString() || '');
    setModifyQty((fill.rmnQty || fill.ordQty)?.toString() || '');
    setShowModifyModal(true);
    setContextMenu(null);
  };

  // 주문 정정 실행
  const executeModifyOrder = async () => {
    if (!modifyFill) return;

    const price = parseFloat(modifyPrice);
    const qty = parseInt(modifyQty);

    if (isNaN(price) || price <= 0) {
      alert('가격은 0보다 큰 숫자여야 합니다.');
      return;
    }

    if (isNaN(qty) || qty <= 0) {
      alert('수량은 0보다 큰 숫자여야 합니다.');
      return;
    }

    if (qty > (modifyFill.rmnQty || modifyFill.ordQty || 0)) {
      alert(`수량은 잔여수량(${(modifyFill.rmnQty || modifyFill.ordQty)?.toLocaleString()})을 초과할 수 없습니다.`);
      return;
    }

    if (!confirm(`주문을 정정하시겠습니까?\n종목: ${modifyFill.prodNm}\n주문번호: ${modifyFill.ordNo}\n새 가격: ${price.toLocaleString()}\n새 수량: ${qty.toLocaleString()}`)) {
      return;
    }

    try {
      const response = await axiosInstance.post('/api/v1/pfbs/fill-modify', {
        ordNo: modifyFill.ordNo,
        orgOrdNo: modifyFill.orgOrdNo,
        prodId: modifyFill.prodId,
        ls: modifyFill.ls,
        lsNm: modifyFill.lsNm,
        qty: qty,
        ordQty: modifyFill.ordQty,
        prc: price,
        ordPrc: modifyFill.ordPrc,
      });

      if (response.data.success) {
        alert('주문 정정이 완료되었습니다.');
        // 데이터 새로고침
        fetchFills(apiPfId, selectedDate, selectedDate);
        fetchPnl(apiPfId, selectedDate);
        fetchOrders(apiPfId, selectedDate, selectedDate);
        setShowModifyModal(false);
        setModifyFill(null);
      } else {
        alert(`주문 정정 실패: ${response.data.error || '알 수 없는 오류'}`);
      }
    } catch (error: any) {
      console.error('주문 정정 에러:', error);
      alert(`주문 정정 실패: ${error.response?.data?.error || error.message || '알 수 없는 오류'}`);
    }
  };

  // KIS API에서 포지션 업데이트
  const updatePosition = () => {
    setError(null); // 에러 초기화

    // "전체"(pfId=0)는 apiPfId 가 undefined 라 서버에 pfId 가 전송되지 않고,
    // /pfbs/update-position 은 KIS API를 계좌별로 호출하므로 400 "pfId is required" 가 된다.
    // 주문 버튼과 동일하게 개별 계좌 선택을 요구한다.
    if (portfolioId === 0) {
      setError('포지션 업데이트는 개별 계좌를 선택해야 합니다.');
      return;
    }

    callAxios({
      axiosInstance: slowAxios,   // KIS 왕복이라 10초로는 모자란다
      m,
      logger,
      url: '/api/v1/pfbs/update-position',
      // ⚠ POST 에서 params 는 **본문(data)** 으로 간다 (tmslib callAxios).
      //   엔드포인트는 pfId 를 **쿼리스트링**으로 받으므로(long? pfId) 본문에 넣으면
      //   바인딩되지 않아 "pfId is required" 400 이 난다 (2026-09-29 실측:
      //   ?pfId=6 → 200, body {"pfId":6} → 400).
      //   그래서 queryParams 로 보낸다. update-fill 도 같은 이유로 그렇게 한다.
      params: {},                       // CallAxiosArgs 필수 필드 (본문은 비운다)
      queryParams: { pfId: apiPfId },
      onSuccess: (data) => {
        console.log('✅ Update Position API 응답:', data);
        if (data && typeof data === 'object' && 'success' in data) {
          const result = data as any;
          if (result.success) {
            console.log(`✅ 포지션 업데이트 완료 - Positions: ${result.positions}`);
            setError(null); // 성공 시 에러 초기화
            // 업데이트 후 최신 데이터 조회
            fetchOrders(apiPfId, selectedDate, selectedDate);
            fetchPositions(apiPfId, selectedDate);
            fetchRecentBalance(balancePfId, selectedDate);
            fetchFills(apiPfId, selectedDate, selectedDate);
            fetchPnl(apiPfId, selectedDate);
          } else {
            const errorMsg = result.error || '포지션 업데이트에 실패했습니다.';
            console.error('❌ 포지션 업데이트 실패:', errorMsg);
            setError(`포지션 업데이트 실패: ${errorMsg}`);
          }
        } else {
          setError('예상치 못한 응답 형식입니다.');
        }
      },
      onError: (errMsgs, err) => {
        // 백엔드에서 반환한 에러 메시지 추출
        let errorMsg = '';
        if (err?.response?.data) {
          const errorData = err.response.data as any;
          if (errorData.error) {
            errorMsg = errorData.error;
          } else if (errorData.errors && Array.isArray(errorData.errors)) {
            errorMsg = errorData.errors.join(', ');
          } else if (typeof errorData === 'string') {
            errorMsg = errorData;
          }
        }
        
        if (!errorMsg && errMsgs && errMsgs.length > 0) {
          errorMsg = errMsgs.join(', ');
        }
        
        if (!errorMsg) {
          errorMsg = err?.message || 'API 연결에 실패했습니다.';
        }
        
        console.error('⚠️ Update Position API 연결 실패:', errorMsg, err);
        setError(`포지션 업데이트 오류: ${errorMsg}`);
      },
      isGet: false
    });
  };

  // KIS API에서 체결 데이터 업데이트
  const updatePfFill = () => {
    setError(null); // 에러 초기화

    // update-position 과 같다 — "전체"(pfId=0)는 계좌를 특정할 수 없어 400 이 난다.
    if (portfolioId === 0) {
      setError('체결 데이터 업데이트는 개별 계좌를 선택해야 합니다.');
      return;
    }

    const endDate = new Date(selectedDate);
    const startDate = new Date(endDate);
    startDate.setDate(startDate.getDate() - 7); // 최근 7일간의 데이터

    callAxios({
      axiosInstance: slowAxios,   // KIS 왕복이라 10초로는 모자란다
      m,
      logger,
      url: '/api/v1/pfbs/update-fill',
      // pfId 도 쿼리스트링이다 (위 update-position 주석 참조).
      params: {},                       // CallAxiosArgs 필수 필드 (본문은 비운다)
      queryParams: {
        pfId: apiPfId,
        startDate: startDate.toISOString().split('T')[0],
        endDate: endDate.toISOString().split('T')[0]
      },
      onSuccess: (data) => {
        console.log('✅ Update PfFill API 응답:', data);
        if (data && typeof data === 'object' && 'success' in data) {
          const result = data as any;
          if (result.success) {
            console.log(`✅ 체결 데이터 업데이트 완료 - Fills: ${result.savedFills}`);
            setError(null); // 성공 시 에러 초기화
            // 업데이트 후 최신 데이터 조회 (조회일자만)
            fetchOrders(apiPfId, selectedDate, selectedDate);
            fetchPositions(apiPfId, selectedDate);
            fetchRecentBalance(balancePfId, selectedDate);
            fetchFills(apiPfId, selectedDate, selectedDate);
            fetchPnl(apiPfId, selectedDate);
          } else {
            const errorMsg = result.error || '체결 데이터 업데이트에 실패했습니다.';
            console.error('❌ 체결 데이터 업데이트 실패:', errorMsg);
            setError(`체결 데이터 업데이트 실패: ${errorMsg}`);
          }
        } else {
          setError('예상치 못한 응답 형식입니다.');
        }
      },
      onError: (errMsgs, err) => {
        // 백엔드에서 반환한 에러 메시지 추출
        let errorMsg = '';
        if (err?.response?.data) {
          const errorData = err.response.data as any;
          if (errorData.error) {
            errorMsg = errorData.error;
          } else if (errorData.errors && Array.isArray(errorData.errors)) {
            errorMsg = errorData.errors.join(', ');
          } else if (typeof errorData === 'string') {
            errorMsg = errorData;
          }
        }
        
        if (!errorMsg && errMsgs && errMsgs.length > 0) {
          errorMsg = errMsgs.join(', ');
        }
        
        if (!errorMsg) {
          errorMsg = err?.message || 'API 연결에 실패했습니다.';
        }
        
        console.error('⚠️ Update PfFill API 연결 실패:', errorMsg, err);
        setError(`체결 데이터 업데이트 오류: ${errorMsg}`);
      },
      isGet: false
    });
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);

        // 일자/계좌 변경 시 슬라이스/체결 필터 초기화
        setSlices([]);
        setSliceOrderId(null);
        setFilteredFills([]);
        setFillsFiltered(false);

        fetchOrders(apiPfId, selectedDate, selectedDate);
        fetchPositions(apiPfId, selectedDate);
        fetchRecentBalance(balancePfId, selectedDate);
        fetchFills(apiPfId, selectedDate, selectedDate);
        fetchPnl(apiPfId, selectedDate);

        setError(null);
      } catch (err) {
        console.error('❌ Portfolio Overview 데이터 로드 실패:', err);
        setError('Portfolio Overview 데이터를 불러오는데 실패했습니다.');
        setOrders([]);
        setPositions([]);
      } finally {
        setLoading(false);
      }

      // 백그라운드: KIS API로 포지션/체결 업데이트 → 완료 후 재조회
      if (apiPfId) {
        (async () => {
          try {
            await axiosInstance.post('/api/v1/pfbs/update-position', {}, { params: { pfId: apiPfId }, timeout: 30000 });
            const endDate = selectedDate;
            const startDate = new Date(new Date(endDate).getTime() - 7 * 86400000).toISOString().split('T')[0];
            await axiosInstance.post('/api/v1/pfbs/update-fill', {}, { params: { pfId: apiPfId, startDate, endDate }, timeout: 30000 });
            // 업데이트 후 재조회
            fetchPositions(apiPfId, selectedDate);
            fetchRecentBalance(balancePfId, selectedDate);
            fetchFills(apiPfId, selectedDate, selectedDate);
            fetchPnl(apiPfId, selectedDate);
          } catch { /* 백그라운드 실패 무시 */ }
        })();
      }
    };

    loadData();
  }, [portfolioId, selectedDate]);

  // order가 변경되면 formData 초기화 (수정 모드)
  useEffect(() => {
    if (selectedOrder) {
      const t0Time = selectedOrder.t0 ? new Date(selectedOrder.t0).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false }) : '09:00';
      const t1Time = selectedOrder.t1 ? new Date(selectedOrder.t1).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false }) : '';

      let prcTy: 'Opp' | 'My' | 'Limit' | 'Stop' = 'Opp';
      let oppLmtIdx = 1;
      let myLmtIdx = 1;
      let limitPrc = null as number | null;
      let stopPrc = null as number | null;

      if (selectedOrder.limitPrc) {
        prcTy = 'Limit';
        limitPrc = selectedOrder.limitPrc;
      } else if (selectedOrder.stopPrc) {
        prcTy = 'Stop';
        stopPrc = selectedOrder.stopPrc;
      } else if (selectedOrder.limitIdx) {
        if (selectedOrder.limitIdx > 0) {
          prcTy = 'Opp';
          oppLmtIdx = selectedOrder.limitIdx;
        } else {
          prcTy = 'My';
          myLmtIdx = Math.abs(selectedOrder.limitIdx);
        }
      }

      setFormData({
        date: selectedOrder.d,
        prodId: selectedOrder.prodId || '',
        prodNm: selectedOrder.prodNm || '',
        isBuy: selectedOrder.ls === 1,
        wei: ((selectedOrder.wei || 0) * 100), // DB 소수점 → 화면 퍼센트 (0.05 → 5%)
        qty: selectedOrder.qty2ord && selectedOrder.qty2ord > 0 ? selectedOrder.qty2ord : null,
        msg: selectedOrder.msg || '',
        exitAll: selectedOrder.exitAll || false,
        prcTy,
        oppLmtIdx,
        myLmtIdx,
        limitPrc,
        stopPrc,
        isSlice: !!selectedOrder.t1,
        hm0: selectedOrder.hm0 || t0Time,
        hm1: selectedOrder.hm1 || t1Time,
        nSlices: selectedOrder.nSlices ?? null
      });
      setShowOrderForm(true);
    } else {
      // 신규 모드: 폼 초기화
      const todayStr = new Date().toISOString().split('T')[0];
      setFormData({
        date: todayStr,
        prodId: '',
        prodNm: '',
        isBuy: true,
        wei: 0,
        qty: null,
        msg: '',
        exitAll: false,
        prcTy: 'Opp',
        oppLmtIdx: 1,
        myLmtIdx: 1,
        limitPrc: null,
        stopPrc: null,
        isSlice: false,
        hm0: getSliceStartTime(todayStr),
        hm1: '15:20',
        nSlices: null
      });
    }
    setErrors({});
  }, [selectedOrder]);

  // call 함수 구현 (주문 관련 API 호출)
  const call = (func: string, params: any, onSuccess: (data: any) => void, onError?: (errMsgs: string[], err: any) => void) => {
    console.log(`API Call: ${func}`, params);
    
    callAxios({
      axiosInstance,
      m,
      logger,
      url: `/api/v1/orders/${func.toLowerCase()}`,
      params,
      onSuccess,
      onError,
      isGet: false
    });
  };

  const getSliceStartTime = (dateStr: string) => {
    const today = new Date().toISOString().split('T')[0];
    if (dateStr === today) {
      const now = new Date();
      return `${String(now.getHours()).padStart(2,'0')}:${String(now.getMinutes()).padStart(2,'0')}`;
    }
    return '09:00';
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => {
      const next = { ...prev, [field]: value };
      // 날짜를 바꾸면 시작시간을 다시 계산한다.
      //   예전에는 isSlice 일 때만 했다. 그래서 일반 주문으로 내일 날짜를 고르면
      //   현재 시각(예: 21:31)이 그대로 남아 t0 가 장 시간 밖이 됐고,
      //   다음날 09:00 에 슬라이스가 만들어지지 않았다 (2026-09-15).
      if (field === 'date') {
        next.hm0 = getSliceStartTime(value);
      }
      return next;
    });
    if (field === 'date' && nonBizDays.includes(value)) {
      setErrors(prev => ({ ...prev, date: '휴일에는 주문을 입력할 수 없습니다' }));
    } else if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleOrderSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // 유효성 검사
    const newErrors: {[key: string]: string} = {};
    
    // 과거 날짜 체크
    const today = new Date().toISOString().split('T')[0];
    if (formData.date < today) {
      newErrors.date = '과거 날짜에는 주문을 입력할 수 없습니다';
    }
    // 휴일(비영업일) 체크
    if (nonBizDays.includes(formData.date)) {
      newErrors.date = '휴일에는 주문을 입력할 수 없습니다';
    }
    if (!formData.prodId) {
      newErrors.prodId = '종목을 선택하세요';
    }
    if (!formData.exitAll && formData.wei <= 0 && (!formData.qty || formData.qty <= 0)) {
      newErrors.wei = '비중 또는 수량을 입력하세요';
    }
    if (formData.prcTy === 'Opp' && (!formData.oppLmtIdx || formData.oppLmtIdx < 1 || formData.oppLmtIdx > 10)) {
      newErrors.oppLmtIdx = '상대호가는 1-10 사이여야 합니다';
    }
    if (formData.prcTy === 'My' && (!formData.myLmtIdx || formData.myLmtIdx < 1 || formData.myLmtIdx > 10)) {
      newErrors.myLmtIdx = '자기호가는 1-10 사이여야 합니다';
    }
    if (formData.prcTy === 'Limit' && !formData.limitPrc) {
      newErrors.limitPrc = '지정가를 입력하세요';
    }
    if (formData.prcTy === 'Stop' && !formData.stopPrc) {
      newErrors.stopPrc = 'STOP 가격을 입력하세요';
    }
    if (formData.isSlice && formData.hm0 && formData.hm1 && formData.hm1 <= formData.hm0) {
      newErrors.hm1 = '종료시간이 시작시간보다 늦어야 합니다';
    }
    if (!selectedOrder && pfOrderSlots && !orderSlotId) {
      newErrors.slot = '주문할 슬롯을 선택하세요';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // TAT 패턴의 주문 파라미터 생성
    if (selectedOrder) {
      // 수정 모드
      if (!selectedOrder.id) {
        toast('주문 ID가 없습니다');
        return;
      }

      const modifyParams: ModifyOrderParams = {
        ordId: selectedOrder.id,
        wei: formData.wei / 100, // 입력값을 100으로 나누어 저장
        qty: formData.qty && formData.qty > 0 ? formData.qty : null,
        exitAll: formData.exitAll,
        limitPrc: formData.prcTy === 'Limit' ? formData.limitPrc : null,
        limitIdx: formData.prcTy === 'Opp' ? formData.oppLmtIdx :
                  formData.prcTy === 'My' ? -(formData.myLmtIdx || 0) : null,
        stopPrc: formData.prcTy === 'Stop' ? formData.stopPrc : null,
        hm0: formData.hm0,
        hm1: formData.isSlice ? formData.hm1 : null,
        nSlices: formData.isSlice ? formData.nSlices : null,
        msg: formData.msg || null,
      };

      call('modify', modifyParams, (data) => {
        toast('주문 정정 완료');
        setShowOrderForm(false);
        setSelectedOrder(null);
        // 주문 목록 다시 로드
        loadOrders();
        // 수량·전량청산이 바뀌면 주문가능수량도 달라진다 (2026-09-16)
        fetchPositions(apiPfId, selectedDate);
      });
    } else {
      // 신규 모드
      const addParams: AddOrderParams = {
        d: formData.date,
        pfId: portfolioId,
        prodId: formData.prodId,
        ls: 1, // L (롱) 고정
        ex: formData.isBuy ? 1 : -1, // 매수=진입(1), 매도=청산(-1)
        wei: formData.wei / 100, // 입력값을 100으로 나누어 저장
        qty: formData.qty && formData.qty > 0 ? formData.qty : null,
        exitAll: formData.exitAll,
        limitPrc: formData.prcTy === 'Limit' ? formData.limitPrc : null,
        limitIdx: formData.prcTy === 'Opp' ? formData.oppLmtIdx :
                  formData.prcTy === 'My' ? -(formData.myLmtIdx || 0) : null,
        stopPrc: formData.prcTy === 'Stop' ? formData.stopPrc : null,
        hm0: formData.hm0,
        hm1: formData.isSlice ? formData.hm1 : null,
        nSlices: formData.isSlice ? formData.nSlices : null,
        msg: formData.msg || null,
        pfStratId: orderSlotId,
      };

      call('add', addParams, (data) => {
        toast('주문 추가 완료');
        setShowOrderForm(false);
        // 주문 목록 다시 로드
        loadOrders();
        // 전량청산 주문은 주문가능수량을 즉시 묶는다 — 포지션도 다시 읽는다 (2026-09-16)
        fetchPositions(apiPfId, selectedDate);
      });
    }
  };

  const handleCancel = (order?: PfOrder) => {
    const target = order || selectedOrder;
    if (!target || !target.id) {
      toast('취소할 주문이 없습니다');
      return;
    }

    if (!window.confirm(`주문을 취소하시겠습니까?\n종목: ${target.prodId} ${target.prodNm || ''}\nID: ${target.id}`)) {
      return;
    }

    const cancelParams: CancelOrderParams = {
      ordId: target.id
    };

    call('cancel', cancelParams,
      (data) => {
        toast('주문 취소 완료');
        setShowOrderForm(false);
        setSelectedOrder(null);
        loadOrders();
        // 취소하면 주문가능수량(ordPsblQty)이 돌아오므로 포지션도 다시 읽는다.
        // 예전에는 loadOrders() 만 불러 포지션 그리드가 그대로였다 (2026-09-16).
        fetchPositions(apiPfId, selectedDate);
      },
      (errMsgs, err) => {
        toast(`주문 취소 실패: ${errMsgs.join(', ')}`);
      }
    );
  };

  const loadOrders = () => {
    fetchOrders(apiPfId, selectedDate, selectedDate);
  };

  // SignalR: 주문 상태 변경 실시간 수신
  const pfIdRef = useRef(portfolioId);
  const dateRef = useRef(selectedDate);
  // 열려 있는 슬라이스 상세의 주문 ID — SignalR 핸들러에서 재조회에 쓴다.
  const sliceOrdRef = useRef<number | null>(null);
  pfIdRef.current = portfolioId;
  dateRef.current = selectedDate;
  sliceOrdRef.current = sliceOrderId;

  useEffect(() => {
    const apiConfig = getApiConfig();
    const signalRUrl = `${apiConfig.BASE_URL}/hubs/realtime-price`;

    const connection = new signalR.HubConnectionBuilder()
      .withUrl(signalRUrl, { accessTokenFactory: () => localStorage.getItem('fds_token') ?? '' })
      .withAutomaticReconnect([3000, 6000, 12000, 30000])
      .configureLogging(signalR.LogLevel.Warning)
      .build();

    connection.on('OrderUpdated', (_data: any) => {
      console.log('[SignalR] OrderUpdated:', _data?.action);
      fetchOrders(pfIdRef.current, dateRef.current, dateRef.current);
      fetchFills(pfIdRef.current, dateRef.current, dateRef.current, false);

      // 체결이 나면 보유수량도 바뀐다. PositionUpdated 는 60초 주기라
      // 그때까지 포지션 그리드가 옛 값으로 남아 있었다 (2026-09-17).
      fetchPositions(pfIdRef.current, dateRef.current);

      // 슬라이스 상세가 열려 있으면 그 주문의 슬라이스도 다시 읽는다.
      // 예전에는 주문 행을 다시 클릭해야만 갱신됐다.
      if (sliceOrdRef.current != null) fetchSlices(sliceOrdRef.current);
    });

    connection.on('PositionUpdated', (_data: any) => {
      console.log('[SignalR] PositionUpdated:', _data?.action, `pos=${_data?.positions}, bs=${_data?.balances}`);
      fetchPositions(pfIdRef.current, dateRef.current);
      fetchRecentBalance(pfIdRef.current, dateRef.current);
    });

    connection.start()
      .then(() => console.log('[SignalR] 실시간 연결 완료'))
      .catch(err => console.error('[SignalR] 연결 실패:', err));

    return () => {
      connection.stop();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 포지션에서 매수/매도 주문 팝업 열기
  const openOrderFromPos = (pos: PfPos, isBuy: boolean) => {
    const prodId = pos.prodid || pos.prodId || '';
    const prodNm = pos.prodNm || '';
    setFormData({
      date: selectedDate,
      prodId,
      prodNm,
      isBuy,
      wei: 0,
      qty: null,
      msg: '',
      exitAll: !isBuy, // 매도면 전량청산 기본 체크
      prcTy: 'Opp', // 기본 = 상대1호가 (2026-09-28 사용자 요청: 자기1 → 상대1)
      oppLmtIdx: 1,
      myLmtIdx: 1,
      limitPrc: null,
      stopPrc: null,
      isSlice: false,
      hm0: getSliceStartTime(selectedDate),
      hm1: '15:20',
      nSlices: null,
    });
    setSelectedOrder(null);
    setShowOrderForm(true);
    setPosContextMenu(null);
  };

  // 전략 슬롯 (2026-10-23) — 상단 슬롯 그리드에서 고르면 주문·체결·슬라이스가 모두 그 슬롯으로 걸러진다.
  //   slotFilter: '' = 전체 · '__none' = 슬롯 미연결 · 그 외 = slotId
  //   시스템 슬롯(ENS·MANUAL·TA)도 그대로 보인다 — 실제로 나간 주문이라 숨기면 합계가 안 맞는다.
  const matchSlot = (slotId: number | null | undefined, f: string) =>
    !f || (f === '__none' ? slotId == null : String(slotId ?? '') === f);

  // 슬롯 평가금액·손익 (2026-10-30) — 전략관리 화면과 같은 /pfstrat/summary (최근 10영업일, 체결 평균단가 누적).
  //   평가금액 = 보유수량 × 그날 마지막 분봉 종가 · 당일손익 = 그날 몫 · 누적손익 = 10일 합 (수수료·세금 추정 차감)
  //   시스템 슬롯(MANUAL·ENS·TA)은 summary 가 주지 않는다 → '-'
  type SlotPerf = { evalAmt: number; dayPnl: number; cumPnl: number; retPct: number | null };
  const [slotPerf, setSlotPerf] = useState<Record<string, SlotPerf & { name: string }>>({});
  useEffect(() => {
    if (!apiPfId) { setSlotPerf({}); return; }
    let alive = true;
    axiosInstance.get('/api/v1/pfstrat/summary', { params: { pfId: apiPfId, endD: selectedDate, days: 10 } })
      .then(res => {
        if (!alive) return;
        const day = ((res.data?.daily ?? []) as { d: string; bySlot: Record<string, number> }[])
          .find(x => x.d === selectedDate);
        const out: Record<string, SlotPerf & { name: string }> = {};
        ((res.data?.slots ?? []) as any[]).forEach(s => {
          out[String(s.id)] = {
            name: s.name,
            evalAmt: ((s.holdings ?? []) as { pos: number; px: number }[]).reduce((a, h) => a + h.pos * h.px, 0),
            dayPnl: day?.bySlot?.[String(s.id)] ?? 0,
            cumPnl: s.total ?? 0,
            retPct: s.retPct ?? null,
          };
        });
        setSlotPerf(out);
      })
      .catch(() => { if (alive) setSlotPerf({}); });
    return () => { alive = false; };
  // 체결 건수가 바뀌면 다시 계산한다 (체결 그리드가 갱신될 때)
  }, [apiPfId, selectedDate, fills.length]);

  const slotRows = useMemo(() => {
    type R = { key: string; slotId: number | null; name: string; kind: string; desc: string;
      nOrd: number; nFilledOrd: number; nRej: number; nFill: number; buyAmt: number; sellAmt: number;
      evalAmt?: number; dayPnl?: number; cumPnl?: number; retPct?: number | null };
    const SYS: Record<string, string> = {
      ENS: '신호 합산 — 슬롯 전환(10-23 13:12) 전 인트라데이 주문',
      MANUAL: '수동 주문 · 슬롯 미지정',
      TA: 'TradingAgents 주문',
    };
    const m = new Map<string, R>();
    const get = (slotId: number | null | undefined, nm: string | null | undefined) => {
      const key = slotId == null ? '__none' : String(slotId);
      let r = m.get(key);
      if (!r) {
        const name = slotId == null ? '(슬롯 없음)' : (nm ?? `#${slotId}`);
        r = { key, slotId: slotId ?? null, name,
          kind: slotId == null ? '미연결' : SYS[name] ? '시스템' : '전략',
          desc: slotId == null ? '우리 주문 기록과 연결 안 되는 체결 (이중 발주 등)' : (SYS[name] ?? ''),
          nOrd: 0, nFilledOrd: 0, nRej: 0, nFill: 0, buyAmt: 0, sellAmt: 0 };
        m.set(key, r);
      }
      return r;
    };
    orders.forEach(o => {
      const r = get(o.slotId, o.slotName);
      r.nOrd++;
      if ((o.cumQty ?? 0) > 0) r.nFilledOrd++;
      if (((o.state as number) ?? 0) < 0) r.nRej++;
    });
    fills.forEach(f => {
      const r = get(f.slotId, f.slotName);
      r.nFill++;
      if (f.lsNm === '매수') r.buyAmt += f.totFillAmt ?? 0; else r.sellAmt += f.totFillAmt ?? 0;
    });
    // 오늘 주문이 없어도 보유가 있거나 그날 손익이 있는 슬롯은 보여 준다
    Object.entries(slotPerf).forEach(([id, p]) => {
      if (!m.has(id) && (p.evalAmt !== 0 || p.dayPnl !== 0)) get(Number(id), p.name);
    });
    m.forEach(r => {
      const p = r.slotId == null ? undefined : slotPerf[String(r.slotId)];
      if (p) { r.evalAmt = p.evalAmt; r.dayPnl = p.dayPnl; r.cumPnl = p.cumPnl; r.retPct = p.retPct; }
    });
    const rank: Record<string, number> = { 전략: 0, 시스템: 1, 미연결: 2 };
    return [...m.values()].sort((a, b) => rank[a.kind] - rank[b.kind] || a.name.localeCompare(b.name));
  }, [orders, fills, slotPerf]);
  const slotTotalRow = useMemo(() => {
    if (slotRows.length === 0) return [];
    const sum = (k: 'evalAmt' | 'dayPnl' | 'cumPnl') =>
      slotRows.some(r => r[k] != null) ? slotRows.reduce((a, r) => a + (r[k] ?? 0), 0) : undefined;
    return [{
      key: '', slotId: null, name: '전체', kind: '', desc: '',
      nOrd: orders.length, nFilledOrd: slotRows.reduce((a, r) => a + r.nFilledOrd, 0),
      nRej: slotRows.reduce((a, r) => a + r.nRej, 0), nFill: fills.length,
      buyAmt: slotRows.reduce((a, r) => a + r.buyAmt, 0), sellAmt: slotRows.reduce((a, r) => a + r.sellAmt, 0),
      evalAmt: sum('evalAmt'), dayPnl: sum('dayPnl'), cumPnl: sum('cumPnl'), retPct: null,
    }];
  }, [slotRows, orders, fills]);
  useEffect(() => {
    if (slotFilter && !slotRows.some(r => r.key === slotFilter)) setSlotFilter('');
  }, [slotRows, slotFilter]);
  const slotFilterNm = slotRows.find(r => r.key === slotFilter)?.name ?? '';
  const ordersView = useMemo(
    () => orders.filter(o => matchSlot(o.slotId, slotFilter)),
    [orders, slotFilter]);
  const fillsView = useMemo(
    () => (fillsFiltered ? filteredFills : fills).filter(f => matchSlot(f.slotId, slotFilter)),
    [fills, filteredFills, fillsFiltered, slotFilter]);

  // 슬롯을 고르면 슬라이스도 그 슬롯 주문 전체로 바꾼다 (주문 행 클릭 = 그 주문만, 기존 동작)
  const [sliceSlotNm, setSliceSlotNm] = useState<string | null>(null);
  const selectSlot = async (key: string) => {
    setSlotFilter(key);
    setFillsFiltered(false);
    setSliceOrderId(null);
    if (!key) { setSlices([]); setSliceSlotNm(null); return; }
    setSliceSlotNm(slotRows.find(r => r.key === key)?.name ?? key);
    try {
      const res = await axiosInstance.get('/api/v1/pfbs/slices', {
        params: { pfId: apiPfId, d: selectedDate, slotId: key === '__none' ? 0 : key },
      });
      setSlices(Array.isArray(res.data) ? res.data as KisPfSlice[] : []);
    } catch { setSlices([]); }
  };
  // 계좌·날짜가 바뀌면 슬롯 선택을 푼다 — 슬롯 슬라이스가 옛 날짜 것으로 남지 않게
  useEffect(() => {
    setSlotFilter(''); setSliceSlotNm(null);
  }, [apiPfId, selectedDate]);
  const slotGridCols = useMemo<ColDef[]>(() => [
    { field: 'name', headerName: '슬롯', width: 120, cellStyle: { fontWeight: 600, color: '#6d28d9' } },
    { field: 'kind', headerName: '구분', width: 70, cellStyle: { textAlign: 'center' } },
    { field: 'nOrd', headerName: '주문', width: 70, cellStyle: { textAlign: 'right' } },
    { field: 'nFilledOrd', headerName: '체결주문', width: 85, cellStyle: { textAlign: 'right' } },
    { field: 'nRej', headerName: '거부', width: 65,
      cellStyle: (p: any) => ({ textAlign: 'right', color: p.value > 0 ? '#dc2626' : undefined }) },
    { field: 'nFill', headerName: '체결', width: 70, cellStyle: { textAlign: 'right' } },
    { field: 'buyAmt', headerName: '매수체결금액', width: 115, cellStyle: { textAlign: 'right', color: '#dc2626' },
      valueFormatter: (p: any) => Math.round(p.value ?? 0).toLocaleString() },
    { field: 'sellAmt', headerName: '매도체결금액', width: 115, cellStyle: { textAlign: 'right', color: '#2563eb' },
      valueFormatter: (p: any) => Math.round(p.value ?? 0).toLocaleString() },
    { field: 'evalAmt', headerName: '평가금액', width: 105, cellStyle: { textAlign: 'right' },
      headerTooltip: '슬롯 보유수량 × 그날 마지막 종가 (최근 10영업일 체결 누적)',
      valueFormatter: (p: any) => p.value == null ? '-' : Math.round(p.value).toLocaleString() },
    { field: 'dayPnl', headerName: '당일손익', width: 95,
      headerTooltip: '조회일 하루 손익 (평가 포함, 수수료·세금 추정 차감)',
      cellStyle: (p: any) => ({ textAlign: 'right', color: pnlColor(p.value) }),
      valueFormatter: (p: any) => p.value == null ? '-' : Math.round(p.value).toLocaleString() },
    { field: 'cumPnl', headerName: '누적손익(10일)', width: 115,
      headerTooltip: '최근 10영업일 누적 손익 — 전략관리 화면과 같은 값',
      cellStyle: (p: any) => ({ textAlign: 'right', color: pnlColor(p.value) }),
      valueFormatter: (p: any) => p.value == null ? '-'
        : Math.round(p.value).toLocaleString() + (p.data?.retPct != null ? ` (${p.data.retPct.toFixed(2)}%)` : '') },
    { field: 'desc', headerName: '설명', flex: 1, minWidth: 200, cellStyle: { color: '#6b7280' } },
  ], []);

  // 주문 내역 컬럼 정의
  const orderColumnDefs = useMemo<ColDef<PfOrder>[]>(() => [
    { field: 'htsId' as any, headerName: 'HTS', width: 70, flex: 0, filter: false, cellStyle: { fontWeight: 600, color: '#1e40af' } },
    { field: 'id', headerName: 'ID', width: 60, flex: 0, filter: false, cellStyle: { textAlign: 'right' } },
    { field: 'slotName', headerName: '슬롯', width: 95, flex: 0, filter: false,
      valueFormatter: (p) => p.value ?? '-', cellStyle: { textAlign: 'center', fontSize: '11px', color: '#6d28d9' } },
    {
      field: 'orderSrc', headerName: '소스', width: 55, flex: 0, filter: false,
      valueFormatter: (params) => params.value === 1 ? '자동' : '수동',
      cellStyle: (params) => ({
        textAlign: 'center', fontSize: '11px', fontWeight: 'bold',
        color: params.value === 1 ? '#8b5cf6' : '#059669',
      }),
    },
    { field: 'd', headerName: '날짜', width: 100, flex: 0, filter: false, cellStyle: { textAlign: 'center' },
      sort: 'asc', sortIndex: 0 },
    {
      // 시작~종료를 한 칸에 붙여 보여준다 (2026-09-15).
      //   단건(즉시 실행) : "09:00"
      //   분할(TWAP)      : "09:00~15:20"
      // 종료(t1)는 주문 입력에서 **[분할 실행]** 을 골랐을 때만 저장된다.
      // [즉시 실행] 이면 hm1=null 로 보내므로 t1 이 비어 있는 게 정상이다.
      field: 't0', headerName: '시간', width: 100, flex: 0, filter: false,
      sort: 'asc', sortIndex: 1,
      valueGetter: (params) => {
        const hhmm = (v: any) => {
          if (!v) return '';
          try {
            return new Date(v).toLocaleTimeString('ko-KR',
              { hour: '2-digit', minute: '2-digit', hour12: false });
          } catch { return ''; }
        };
        const s = params.data?.hm0 || hhmm(params.data?.t0);
        const e = params.data?.hm1 || hhmm(params.data?.t1);
        if (!s) return '';
        return e ? `${s}~${e}` : s;
      },
      cellStyle: { textAlign: 'center' },
    },
    { field: 'prodId', headerName: '종목', width: 75, flex: 0, filter: false,
      // 클릭 → 아래 분봉 차트에 이 종목의 주문·체결 표식을 올린다
      onCellClicked: (e: any) => {
        if (e.value) {
          setChartStock({ prodId: String(e.value), prodNm: e.data?.prodNm || String(e.value) });
          setUserPickedStock(true);
        }
      },
      cellStyle: { textAlign: 'left', cursor: 'pointer', color: '#1d4ed8', textDecoration: 'underline' } },
    { field: 'prodNm', headerName: '종목명', minWidth: 80, flex: 1, filter: false, cellStyle: { textAlign: 'left' } },
    {
      field: 'ls', headerName: 'LS', width: 38, flex: 0, filter: false,
      valueFormatter: (params) => params.value === 1 ? 'L' : 'S',
      cellStyle: (params) => ({ color: params.value === 1 ? '#22c55e' : '#ef4444', fontWeight: 'bold', textAlign: 'center' }),
    },
    {
      field: 'ex', headerName: 'EX', width: 38, flex: 0, filter: false,
      valueFormatter: (params) => params.value === 1 ? 'E' : 'X',
      cellStyle: (params) => ({ color: params.value === 1 ? '#3b82f6' : '#f59e0b', fontWeight: 'bold', textAlign: 'center' }),
    },
    {
      headerName: '구분', width: 55, flex: 0, filter: false,
      valueGetter: (params) => {
        const qty = params.data?.qty2ord ?? params.data?.qty2Ord ?? 0;
        const wei = params.data?.wei ?? 0;
        // 수량 직접 입력이고 비중이 0이면 수량 주문
        if (qty > 0 && wei === 0) return '수량';
        return '비중';
      },
      cellStyle: (params) => ({
        textAlign: 'center', fontSize: '11px',
        color: params.value === '수량' ? '#8b5cf6' : '#0ea5e9',
      }),
    },
    {
      field: 'wei', headerName: '비중%', width: 64, flex: 0, filter: false,
      headerTooltip: '순자산(NA) 대비 비중',
      valueFormatter: (params) => params.value != null ? (params.value * 100).toFixed(2) : '',
      cellStyle: { textAlign: 'right' },
    },
    {
      field: 'exitAll', headerName: '전량', width: 37, flex: 0, filter: false,
      valueFormatter: (params) => params.value ? 'T' : '',
      cellStyle: { textAlign: 'center', fontWeight: 'bold', color: '#ef4444' },
    },
    {
      field: 'limitPrc', headerName: '지정가', width: 80, flex: 0, filter: false,
      valueFormatter: (params) => params.value ? params.value.toLocaleString() : '',
      cellStyle: { textAlign: 'right' },
    },
    {
      // 호가 방식 — limitIdx 의 부호가 의미를 가른다 (LimitPrcResolver 참조).
      //   양수(+n) = 상대호가 : 상대편 n번째 호가에 건다 → 바로 체결되는 쪽
      //   음수(-n) = 자기호가 : 내 편 n번째 호가에 건다 → 줄 서서 기다리는 쪽
      // 매수/매도는 ex 로 판정한다(1=진입=매수, -1=청산=매도).
      field: 'limitIdx', headerName: '호가', width: 78, flex: 0, filter: false,
      valueGetter: (params) => {
        const v = params.data?.limitIdx;
        if (v == null || v === 0) return '';
        return v > 0 ? `상대${v}` : `자기${Math.abs(v)}`;
      },
      tooltipValueGetter: (params: any) => {
        const v = params.data?.limitIdx;
        if (v == null || v === 0) return '';
        const isBuy = params.data?.ex === 1;
        const n = Math.abs(v);
        return v > 0
          ? `상대${n}호가 — ${isBuy ? `매도${n}호가에 매수` : `매수${n}호가에 매도`} (즉시체결 성향)`
          : `자기${n}호가 — ${isBuy ? `매수${n}호가에 매수` : `매도${n}호가에 매도`} (대기 성향)`;
      },
      cellStyle: (params) => ({
        textAlign: 'center', fontSize: '11px', fontWeight: 600,
        // 상대호가는 체결 지향(초록), 자기호가는 대기(회색)
        color: (params.data?.limitIdx ?? 0) > 0 ? '#059669' : '#6b7280',
      }),
    },
    {
      field: 't1', headerName: '종료', width: 65, flex: 0, filter: false,
      valueGetter: (params) => {
        if (params.data?.hm1) return params.data.hm1;
        if (params.data?.t1) {
          try { return new Date(params.data.t1).toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit', hour12: false }); } catch { return ''; }
        }
        return '';
      },
      cellStyle: { textAlign: 'center' },
    },
    {
      field: 'state', headerName: '상태', width: 58, flex: 0, filter: false,
      valueFormatter: (params) => {
        const m: { [k: number]: string } = { [-10]: '삭제', [-9]: '거절', 0: '미접수', 10: '접수', 20: '작동', 30: '완료' };
        return m[params.value] || `${params.value}`;
      },
      cellStyle: (params) => {
        const c: { [k: number]: string } = { [-10]: '#9ca3af', [-9]: '#ef4444', 0: '#6b7280', 10: '#3b82f6', 20: '#22c55e', 30: '#f59e0b' };
        return { color: c[params.value] || '#6b7280', fontWeight: 'bold', textAlign: 'center' };
      },
    },
    {
      headerName: '분할', width: 64, flex: 0, filter: false,
      headerTooltip: '시간분할 진행 (완료 슬라이스 / 총 분할 수). nSlices 미지정 시 (t1-t0)/간격 으로 역산',
      valueGetter: (params) => {
        const d = params.data as any;
        if (!d) return '';
        const total = Number(d.sliceTotal ?? 0);
        const done = Number(d.sliceDone ?? 0);
        if (total > 1) return `${done}/${total}`;
        return '';
      },
      cellStyle: (params) => {
        const d = params.data as any;
        const total = Number(d?.sliceTotal ?? 0);
        const done = Number(d?.sliceDone ?? 0);
        return { textAlign: 'center', fontWeight: 600,
          color: total > 1 && done >= total ? '#22c55e' : total > 1 ? '#f59e0b' : '#6b7280' };
      },
    },
    {
      field: 'qty2ord', headerName: '주문', width: 65, flex: 0, filter: false,
      valueFormatter: (params) => params.value ? params.value.toLocaleString() : '',
      cellStyle: { textAlign: 'right' },
    },
    {
      field: 'cumQty', headerName: '체결', width: 65, flex: 0, filter: false,
      valueFormatter: (params) => params.value ? params.value.toLocaleString() : '',
      cellStyle: { textAlign: 'right' },
    },
    {
      headerName: '미체결', width: 65, flex: 0, filter: false,
      valueGetter: (params) => {
        const qty = params.data?.qty2ord ?? params.data?.qty2Ord ?? 0;
        const cum = params.data?.cumQty ?? 0;
        return qty - cum;
      },
      valueFormatter: (params) => params.value != null ? params.value.toLocaleString() : '',
      cellStyle: (params) => ({ textAlign: 'right', fontWeight: 'bold', color: params.value > 0 ? '#f59e0b' : '#22c55e' }),
    },
    {
      field: 'avgPx', headerName: '평균가', width: 85, flex: 0, filter: false,
      valueFormatter: (params) => params.value ? Math.round(params.value).toLocaleString() : '',
      cellStyle: { textAlign: 'right' },
    },
    {
      field: 'cnclT', headerName: '취소', width: 55, flex: 0, filter: false,
      valueFormatter: (params) => {
        if (!params.value) return '';
        try { return new Date(params.value).toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit', hour12: false }); } catch { return ''; }
      },
      cellStyle: (params) => ({ color: params.value ? '#ef4444' : 'inherit', textAlign: 'center' }),
    },
    {
      // 에러메시지·코멘트는 길어서 셀에 다 안 들어간다.
      //   1) 마우스오버 → 툴팁으로 전문
      //   2) 셀 클릭    → 하단 상세 패널에 전문 (복사 가능)
      // 둘 다 두는 이유: 툴팁은 빠르지만 아주 긴 글은 잘리고 복사가 안 된다.
      field: 'errMsg', headerName: '에러메시지', flex: 1, minWidth: 100, filter: false,
      tooltipValueGetter: (params: any) => params.value || '',
      onCellClicked: (e: any) => {
        if (e.value) setMsgDetail({ title: '에러메시지', text: String(e.value) });
      },
      cellStyle: { textAlign: 'left', cursor: 'pointer' },
    },
    {
      field: 'msg', headerName: '코멘트', flex: 1, minWidth: 80, filter: false,
      valueGetter: (params) => {
        if (params.data?.cnclT) return '주문 취소';
        return params.data?.msg || '';
      },
      tooltipValueGetter: (params: any) => params.value || '',
      onCellClicked: (e: any) => {
        if (e.value) setMsgDetail({ title: '코멘트', text: String(e.value) });
      },
      cellStyle: (params) => ({ textAlign: 'left', cursor: 'pointer', color: params.data?.cnclT ? '#ef4444' : 'inherit', fontWeight: params.data?.cnclT ? 'bold' : 'normal' }),
    },
  ], []);

  // 포지션 컬럼 정의 (SQL 쿼리 결과에 맞춤: d, pfid, prodid, pos, avgprc, bamt, c, evluamt, prft, bqtyyd, sqtyyd, bqty, sqty)
  const positionColumnDefs = useMemo<ColDef<PfPos>[]>(() => [
    { field: 'htsId' as any, headerName: 'HTS', width: 70, flex: 0, filter: false, cellStyle: { fontWeight: 600, color: '#1e40af' } },
    {
      field: 'd',
      headerName: '날짜',
      minWidth: 95, flex: 0.9,
      filter: false,
      valueFormatter: (params) => params.value || '',
      cellStyle: { textAlign: 'left' }
    },
    {
      field: 'pfid',
      headerName: '포트폴리오ID',
      minWidth: 60, flex: 0.5,
      filter: false,
      valueFormatter: (params) => params.value ? params.value.toString() : '',
      cellStyle: { textAlign: 'right' }
    },
    {
      field: 'prodid',
      headerName: '종목코드',
      minWidth: 65, flex: 0.6,
      filter: false,
      valueGetter: (params) => params.data?.prodid || params.data?.prodId || '',
      cellStyle: { textAlign: 'left' }
    },
    {
      field: 'prodNm',
      headerName: '종목명',
      minWidth: 80, flex: 1.2,
      filter: false,
      cellStyle: { textAlign: 'left' }
    },
    {
      field: 'pos',
      headerName: '보유수량',
      minWidth: 65, flex: 0.7,
      filter: false,
      valueFormatter: (params) => params.value ? params.value.toLocaleString() : '0',
      valueGetter: (params) => params.data?.pos || params.data?.qty || 0,
      cellStyle: { textAlign: 'right' }
    },
    {
      // 매도가능수량 — 보유수량에서 이미 걸린 매도주문을 뺀 값.
      //
      // 0 이면 **청산 주문이 진행 중**이라 추가 매도를 내면 안 된다.
      // 이 값이 화면에 없던 탓에 보유수량만 보고 미체결인 줄 모른 채
      // 매도를 다시 내는 일이 있었다(2026-09-15). 그래서 0 은 빨갛게 강조한다.
      field: 'ordpsblqty' as any,
      headerName: '매도가능',
      minWidth: 70, flex: 0.7,
      filter: false,
      valueGetter: (params) => params.data?.ordpsblqty ?? null,
      valueFormatter: (params) =>
        params.value == null ? '-' : Number(params.value).toLocaleString(),
      cellStyle: (params: any) => {
        const v = params.data?.ordpsblqty;
        const held = params.data?.pos ?? 0;
        // 보유는 있는데 매도가능이 0 → 주문이 이미 걸려 있다
        if (held > 0 && v === 0) {
          return { textAlign: 'right', color: '#dc2626', fontWeight: 700, backgroundColor: '#fef2f2' };
        }
        return { textAlign: 'right' };
      },
      tooltipValueGetter: (params: any) =>
        (params.data?.pos ?? 0) > 0 && params.data?.ordpsblqty === 0
          ? '이미 청산 주문이 걸려 있다 (미체결) — 추가 매도 불가'
          : '',
    },
    {
      field: 'avgprc',
      headerName: '평단가',
      minWidth: 70, flex: 0.8,
      filter: false,
      valueFormatter: (params) => params.value ? Math.round(params.value).toLocaleString() : '',
      valueGetter: (params) => params.data?.avgprc || params.data?.avgPx || null,
      cellStyle: { textAlign: 'right' }
    },
    {
      field: 'bamt',
      headerName: '매수금액 (원)',
      minWidth: 90, flex: 1,
      filter: false,
      valueFormatter: (params) => params.value ? params.value.toLocaleString() : '',
      cellStyle: { textAlign: 'right' }
    },
    {
      field: 'c',
      headerName: '종가',
      minWidth: 65, flex: 0.7,
      filter: false,
      valueFormatter: (params) => params.value ? params.value.toLocaleString() : '',
      valueGetter: (params) => params.data?.c || params.data?.currPx || null,
      cellStyle: { textAlign: 'right' }
    },
    {
      field: 'evluamt',
      headerName: '평가액 (원)',
      minWidth: 90, flex: 1,
      filter: false,
      valueFormatter: (params) => params.value ? params.value.toLocaleString() : '',
      cellStyle: { textAlign: 'right' }
    },
    {
      headerName: '비중(%)',
      headerTooltip: '보유종목 평가금액 합계 대비 비중 = 평가금액/전체평가금액합계',
      minWidth: 60, flex: 0.6,
      filter: false,
      valueGetter: (params) => {
        const evlu = params.data?.evluamt || 0;
        const total = positions.reduce((s, p) => s + (p.evluamt || 0), 0);
        return total > 0 ? (evlu / total * 100) : 0;
      },
      valueFormatter: (params) => params.value != null ? params.value.toFixed(1) : '',
      cellStyle: { textAlign: 'right' }
    },
    {
      field: 'prft',
      headerName: '손익 (원)',
      minWidth: 80, flex: 0.8,
      filter: false,
      valueFormatter: (params) => params.value ? params.value.toLocaleString() : '',
      cellStyle: (params) => ({
        color: (params.value ?? 0) >= 0 ? '#22c55e' : '#ef4444',
        fontWeight: 'bold',
        textAlign: 'right'
      })
    },
    {
      // 실현손익 — 매도로 확정된 손익. KIS 경로에선 없을 수 있어 '-' 표시.
      field: 'prftkrw', headerName: '실현손익',
      minWidth: 80, flex: 0.8, filter: false,
      valueFormatter: (p) => p.value == null ? '-' : Number(p.value).toLocaleString(),
      cellStyle: (p) => ({
        color: (p.value ?? 0) >= 0 ? '#22c55e' : '#ef4444',
        textAlign: 'right',
      }),
    },
    {
      // 수수료·거래세 — 서버 값만 표시. 없으면 '-' (역산하지 않는다).
      //   KIS 잔고API는 계좌 단위 제비용(bfdy_tlex_amt/thdt_tlex_amt)만 주고
      //   종목별 수수료·세금은 주지 않는다. sim 계정만 실제 계산값이 채워진다.
      field: 'fee', headerName: '수수료',
      minWidth: 70, flex: 0.6, filter: false,
      valueFormatter: (p) => p.value == null ? '-' : Number(p.value).toLocaleString(),
      cellStyle: { textAlign: 'right', color: '#6b7280' },
    },
    {
      field: 'tax', headerName: '거래세',
      minWidth: 70, flex: 0.6, filter: false,
      valueFormatter: (p) => p.value == null ? '-' : Number(p.value).toLocaleString(),
      cellStyle: { textAlign: 'right', color: '#6b7280' },
    },
    {
      field: 'bqtyyd',
      headerName: '전일매수수량',
      minWidth: 60, flex: 0.6,
      filter: false,
      valueFormatter: (params) => params.value ? params.value.toLocaleString() : '0',
      cellStyle: { textAlign: 'right' }
    },
    {
      field: 'sqtyyd',
      headerName: '전일매도수량',
      minWidth: 60, flex: 0.6,
      filter: false,
      valueFormatter: (params) => params.value ? params.value.toLocaleString() : '0',
      cellStyle: { textAlign: 'right' }
    },
    {
      field: 'bqty',
      headerName: '매수수량',
      minWidth: 60, flex: 0.6,
      filter: false,
      valueFormatter: (params) => params.value ? params.value.toLocaleString() : '0',
      cellStyle: { textAlign: 'right' }
    },
    {
      field: 'sqty',
      headerName: '매도수량',
      minWidth: 60, flex: 0.6,
      filter: false,
      valueFormatter: (params) => params.value ? params.value.toLocaleString() : '0',
      cellStyle: { textAlign: 'right' }
    },
  ], [positions]);

  // 잔고 컬럼 정의
  /**
   * 손익(pfbs) 그리드 — **바로 위 KIS 잔고 그리드와 대조**가 목적이다.
   * 같은 조회일자의 한 행끼리 비교한다.
   */
  const pnlColumnDefs = useMemo<ColDef<PnlRow>[]>(() => {
    const num = (p: any) => p.value == null ? '-' : Math.round(Number(p.value)).toLocaleString();
    const signColor = (p: any) => ({
      textAlign: 'right' as const, fontWeight: 600,
      color: (p.value ?? 0) >= 0 ? '#22c55e' : '#ef4444',
    });
    return [
      { field: 'htsId', headerName: 'HTS ID', minWidth: 80, flex: 1, cellStyle: { textAlign: 'center' } },
      { field: 'd', headerName: '날짜', minWidth: 90, flex: 1, cellStyle: { textAlign: 'center' } },
      {
        field: 'nFill', headerName: '체결건수', minWidth: 70, flex: 1,
        headerTooltip: '그날 체결 건수',
        cellStyle: { textAlign: 'right', color: '#6b7280' },
      },
      { field: 'buyAmt', headerName: '매수대금', minWidth: 95, flex: 1, cellStyle: { textAlign: 'right' }, valueFormatter: num },
      { field: 'sellAmt', headerName: '매도대금', minWidth: 95, flex: 1, cellStyle: { textAlign: 'right' }, valueFormatter: num },
      {
        field: 'realized', headerName: '체결손익', minWidth: 95, flex: 1,
        headerTooltip: '매도대금 − 매수대금 (비용 전). 체결에서 직접 계산한 값',
        cellStyle: signColor, valueFormatter: num,
      },
      {
        field: 'cost', headerName: '거래비용', minWidth: 80, flex: 1,
        headerTooltip: '수수료+거래세. kispfbs 의 texAmt / texAmtYd 중 값이 있는 쪽',
        cellStyle: { textAlign: 'right', color: '#6b7280' }, valueFormatter: num,
      },
      {
        field: 'net', headerName: '자체계산 손익', minWidth: 110, flex: 1,
        headerTooltip: '체결손익 − 거래비용. 당일 전량청산한 날의 실현손익이다. '
                     + '미청산일(이월 보유가 있는 날)은 이 값이 현금흐름이다 — 매수만 한 날은 매수금액만큼 음수가 된다. 손익이 아니다. 숫자 뒤 * 가 그 표시다.',
        // 미청산일은 손익이 아니라 현금흐름 — 색을 빼고 * 를 붙인다 (2026-10-11)
        cellStyle: (p: any) => (p.data?.flat === false
          ? { textAlign: 'right', color: '#94a3b8', fontWeight: 400 }
          : { ...signColor(p), fontWeight: 700 }),
        valueFormatter: (p: any) => (p.value == null ? '-'
          : Math.round(Number(p.value)).toLocaleString() + (p.data?.flat === false ? ' *' : '')),
      },
      {
        // KIS 가 **직접 준** 값 (asst_icdc_amt). 위 잔고 그리드의 일손익금액과 같다.
        field: 'kisDiffAmt', headerName: 'KIS 일손익', minWidth: 105, flex: 1,
        headerTooltip: 'KIS 가 직접 내려준 일손익(asst_icdc_amt). 위 잔고 그리드의 일손익금액과 같은 값',
        cellStyle: signColor, valueFormatter: num,
      },
      {
        // **검증의 핵심** — 우리 계산과 KIS 원본의 차이.
        field: 'kisGap', headerName: 'KIS 차이', minWidth: 90, flex: 1,
        headerTooltip: '자체계산 − KIS 원본. 0 이면 양쪽이 맞다. '
                     + '0 이 아닌데 NAV검증이 0 이면 KIS 값이 틀린 것이다',
        valueFormatter: (p: any) => {
          if (p.value == null) return '-';   // 미청산이면 애초에 비교 불가
          const v = Math.round(Number(p.value));
          return v === 0 ? '0 ✓' : v.toLocaleString();
        },
        cellStyle: (p: any) => {
          if (p.value == null) return { textAlign: 'right', color: '#9ca3af' };
          const ok = Math.abs(Number(p.value)) < 1;
          return {
            textAlign: 'right', fontWeight: 700,
            color: ok ? '#16a34a' : '#b45309',
            backgroundColor: ok ? '#f0fdf4' : '#fffbeb',
          };
        },
      },
      {
        // 보조 검증 — KIS 순자산 증감(우리가 뺀 값). KIS 원본이 틀렸을 때 어느 쪽이
        // 잘못인지 가려준다: 여기가 0 인데 'KIS 차이' 가 0 이 아니면 KIS 값이 틀린 것.
        field: 'navDiff', headerName: '순자산증감', minWidth: 100, flex: 1,
        headerTooltip: '당일 순자산 − 전일 순자산 (우리가 계산). KIS 원본이 틀렸는지 가리는 보조 지표',
        cellStyle: (p: any) => ({ ...signColor(p), fontWeight: 400 }), valueFormatter: num,
      },
      {
        field: 'diffCheck', headerName: 'NAV검증', minWidth: 85, flex: 1,
        headerTooltip: '자체계산 − 순자산증감. 0 이면 우리 집계가 잔고 움직임과 일치한다',
        valueFormatter: (p: any) => {
          if (p.value == null) return '-';
          const v = Math.round(Number(p.value));
          return v === 0 ? '0 ✓' : v.toLocaleString();
        },
        cellStyle: (p: any) => {
          if (p.value == null) return { textAlign: 'right', color: '#9ca3af' };
          const ok = Math.abs(Number(p.value)) < 1;
          return { textAlign: 'right', fontWeight: 600, color: ok ? '#16a34a' : '#b45309' };
        },
      },
      {
        field: 'flat', headerName: '청산', minWidth: 70, flex: 1,
        headerTooltip: '당일 전량청산 여부. 미청산이면 이월분 미실현이 자체계산에 안 잡힌다',
        cellStyle: { textAlign: 'center' },
        valueFormatter: (p: any) => p.value ? '완료' : '미청산',
      },
      {
        field: 'unrealized', headerName: '평가손익', minWidth: 90, flex: 1,
        headerTooltip: '보유분 미실현. 전량청산한 날은 0',
        cellStyle: signColor, valueFormatter: num,
      },
      {
        field: 'retPct', headerName: '수익률(%)', minWidth: 85, flex: 1,
        cellStyle: signColor,
        valueFormatter: (p: any) => p.value == null ? '-' : `${Number(p.value).toFixed(3)}%`,
      },
      { field: 'nav', headerName: '순자산', minWidth: 100, flex: 1, cellStyle: { textAlign: 'right' }, valueFormatter: num },
      {
        field: 'updAt', headerName: '집계시각', minWidth: 90, flex: 1,
        headerTooltip: 'GenPfBSJob 이 이 행을 마지막으로 계산한 시각 (5분 주기)',
        cellStyle: { textAlign: 'center', color: '#9ca3af' },
      },
    ];
  }, []);

  /**
   * 체결손익 세부 컬럼 — 종목별로 **그 숫자가 어떻게 나왔는지** 보인다.
   * 매수 수량·평단 → 매도 수량·평단 → 손익 순으로, 계산 과정을 따라 읽게 배치했다.
   */
  const tradeDetailCols = useMemo<ColDef<TradePnlRow>[]>(() => {
    const num = (p: any) => p.value == null ? '-' : Math.round(Number(p.value)).toLocaleString();
    const prc = (p: any) => p.value ? Number(p.value).toFixed(2) : '-';
    const signColor = (p: any) => ({
      textAlign: 'right' as const, fontWeight: 700,
      color: (p.value ?? 0) >= 0 ? '#22c55e' : '#ef4444',
    });
    return [
      { field: 'prodId', headerName: '종목코드', minWidth: 85, flex: 1, cellStyle: { textAlign: 'center' } },
      { field: 'prodNm', headerName: '종목명', minWidth: 140, flex: 2, cellStyle: { textAlign: 'left' } },
      { field: 'bQty', headerName: '매수수량', minWidth: 85, flex: 1, cellStyle: { textAlign: 'right' }, valueFormatter: num },
      { field: 'bAvg', headerName: '매수평단', minWidth: 90, flex: 1, cellStyle: { textAlign: 'right' }, valueFormatter: prc },
      { field: 'bAmt', headerName: '매수대금', minWidth: 100, flex: 1, cellStyle: { textAlign: 'right' }, valueFormatter: num },
      { field: 'sQty', headerName: '매도수량', minWidth: 85, flex: 1, cellStyle: { textAlign: 'right' }, valueFormatter: num },
      { field: 'sAvg', headerName: '매도평단', minWidth: 90, flex: 1, cellStyle: { textAlign: 'right' }, valueFormatter: prc },
      { field: 'sAmt', headerName: '매도대금', minWidth: 100, flex: 1, cellStyle: { textAlign: 'right' }, valueFormatter: num },
      {
        field: 'pnl', headerName: '손익', minWidth: 100, flex: 1,
        headerTooltip: '매도대금 − 매수대금. 이 값들의 합이 위 그리드의 체결손익이다',
        cellStyle: signColor, valueFormatter: num,
      },
      {
        field: 'pnlRt', headerName: '수익률(%)', minWidth: 90, flex: 1,
        cellStyle: (p: any) => ({ ...signColor(p), fontWeight: 600 }),
        valueFormatter: (p: any) => p.value == null ? '-' : `${Number(p.value).toFixed(2)}%`,
      },
      {
        field: 'flat', headerName: '청산', minWidth: 70, flex: 1,
        headerTooltip: '매수수량 == 매도수량. 미청산이면 손익이 일부만 실현된 것이다',
        cellStyle: { textAlign: 'center' },
        valueFormatter: (p: any) => p.value ? '완료' : '미청산',
      },
    ];
  }, []);

  const balanceColumnDefs = useMemo<ColDef<PfBs>[]>(() => [
    {
      field: 'htsId' as any,
      headerName: 'HTS ID',
      width: 85, flex: 0,
      filter: false,
      cellStyle: { fontWeight: 600, color: '#1e40af' }
    },
    {
      field: 'd',
      headerName: '날짜',
      minWidth: 95, flex: 0.8,
      filter: false,
      cellStyle: { textAlign: 'left' }
    },
    {
      field: 'totAmt',
      headerName: '예수금총금액',
      minWidth: 100, flex: 1,
      filter: false,
      valueFormatter: (params) => params.value ? params.value.toLocaleString() : '0',
      cellStyle: { textAlign: 'right' }
    },
    {
      field: 'nTotAmt',
      headerName: '익일정산금액',
      minWidth: 100, flex: 1,
      filter: false,
      valueFormatter: (params) => params.value ? params.value.toLocaleString() : '0',
      cellStyle: { textAlign: 'right' }
    },
    {
      field: 'n2TotAmt',
      headerName: '가수도정산금액',
      minWidth: 100, flex: 1,
      filter: false,
      valueFormatter: (params) => params.value ? params.value.toLocaleString() : '0',
      cellStyle: { textAlign: 'right' }
    },
    {
      field: 'stkEvluAmt',
      headerName: '유가평가금액',
      minWidth: 100, flex: 1,
      filter: false,
      valueFormatter: (params) => params.value ? params.value.toLocaleString() : '0',
      cellStyle: { textAlign: 'right' }
    },
    {
      field: 'totEvluAmt',
      headerName: '총평가금액',
      minWidth: 100, flex: 1,
      filter: false,
      valueFormatter: (params) => params.value ? params.value.toLocaleString() : '0',
      cellStyle: { textAlign: 'right', fontWeight: 'bold' }
    },
    {
      field: 'navAmt',
      headerName: '순자산금액',
      minWidth: 100, flex: 1,
      filter: false,
      valueFormatter: (params) => params.value ? params.value.toLocaleString() : '0',
      cellStyle: { textAlign: 'right', fontWeight: 'bold' }
    },
    {
      // 초기투자금액 — 계좌정보(kisapimst.initAmt)에서 가져온다.
      colId: 'initAmt', headerName: '초기투자금',
      minWidth: 100, flex: 1, filter: false,
      valueGetter: (p: any) => initAmtOf(p.data),
      valueFormatter: (p) => p.value ? Number(p.value).toLocaleString() : '-',
      cellStyle: { textAlign: 'right', color: '#6b7280' },
    },
    {
      // 누적손익 = 순자산 − 초기투자금
      colId: 'totalPnl', headerName: '누적손익',
      minWidth: 100, flex: 1, filter: false,
      valueGetter: (p: any) => {
        const init = initAmtOf(p.data);
        return init && p.data?.navAmt ? p.data.navAmt - init : null;
      },
      valueFormatter: (p) => p.value == null ? '-' : Number(p.value).toLocaleString(),
      cellStyle: (p: any) => ({
        textAlign: 'right', fontWeight: 'bold',
        color: (p.value ?? 0) >= 0 ? '#22c55e' : '#ef4444',
      }),
    },
    {
      // 누적수익률 = (순자산 / 초기투자금 − 1) × 100
      colId: 'totalPnlRt', headerName: '누적수익률',
      minWidth: 90, flex: 0, width: 90, filter: false,
      valueGetter: (p: any) => {
        const init = initAmtOf(p.data);
        return init && p.data?.navAmt ? (p.data.navAmt / init - 1) * 100 : null;
      },
      valueFormatter: (p) => p.value == null ? '-' : `${Number(p.value).toFixed(2)}%`,
      cellStyle: (p: any) => ({
        textAlign: 'right', fontWeight: 'bold',
        color: (p.value ?? 0) >= 0 ? '#22c55e' : '#ef4444',
      }),
    },
    {
      // 당일매매 실현손익 (2026-10-07 신설).
      //
      // 평가손익합계는 **보유분 미실현**이다. intraday 가 15:05 에 전량 청산하므로
      // 장이 끝나면 보유가 0 이고 평가손익도 0 이다 — 그 상태로는 "오늘 얼마 벌었나"를
      // 볼 수가 없었다. 이 컬럼은 체결 기준 실현손익(매도대금 − 매수대금 − 비용)이다.
      field: 'tradeNet',
      headerName: '당일매매손익',
      headerTooltip: '체결 기준 실현손익 (매도대금 − 매수대금 − 거래비용). '
                   + '전량청산한 날은 이 값이 그날 손익의 전부다. * = 이월분 있어 미실현 제외',
      minWidth: 110, flex: 1,
      filter: false,
      valueFormatter: (p: any) => {
        if (p.value == null) return '-';
        const partial = p.data?.tradeFlat === false ? ' *' : '';
        return Math.round(Number(p.value)).toLocaleString() + partial;
      },
      cellStyle: (p: any) => ({
        color: (p.value ?? 0) >= 0 ? '#22c55e' : '#ef4444',
        fontWeight: 'bold', textAlign: 'right',
      }),
    },
    {
      field: 'prftSumAmt',
      headerName: '평가손익합계',
      headerTooltip: '보유 중인 종목의 미실현 손익. 전량청산한 날은 보유가 없어 0 이다.',
      minWidth: 100, flex: 1,
      filter: false,
      valueFormatter: (params) => params.value ? params.value.toLocaleString() : '0',
      cellStyle: (params) => ({
        color: (params.value ?? 0) >= 0 ? '#22c55e' : '#ef4444',
        fontWeight: 'bold',
        textAlign: 'right'
      })
    },
    {
      field: 'diffAmt',
      // KIS 원문 용어는 '자산증감액'(asst_icdc_amt)이지만, 실제 의미가
      //   asst_icdc_amt = nass_amt − bfdy_tot_asst_evlu_amt  (순자산 − 전일총자산)
      // 즉 **전일 대비 하루치 손익**이라 화면에는 '일손익금액'으로 적는다.
      // (2026-09-29 실측 검증: 9/21~9/29 전 행에서 위 식이 정확히 일치)
      // 누적손익(= 순자산 − 초기투자금)과 혼동하지 않도록 이름을 나눴다.
      headerName: '일손익금액',
      headerTooltip: 'KIS asst_icdc_amt(자산증감액) — 순자산 − 전일총자산평가금액. 전일 대비 하루치 손익',
      minWidth: 100, flex: 1,
      filter: false,
      valueFormatter: (params) => params.value ? params.value.toLocaleString() : '0',
      cellStyle: (params) => ({
        color: (params.value ?? 0) >= 0 ? '#22c55e' : '#ef4444',
        fontWeight: 'bold',
        textAlign: 'right'
      })
    },
  ], []);

  // 체결 내역 컬럼 정의
  const fillColumnDefs = useMemo<ColDef<KisPfFill>[]>(() => [
    { field: 'htsId' as any, headerName: 'HTS', width: 70, flex: 0, filter: false, cellStyle: { fontWeight: 600, color: '#1e40af' } },
    { field: 'slotName', headerName: '슬롯', width: 95, flex: 0, filter: false,
      valueFormatter: (p) => p.value ?? '-', cellStyle: { textAlign: 'center', fontSize: '11px', color: '#6d28d9' } },
    { field: 'd', headerName: '날짜', width: 100, flex: 0, filter: false, cellStyle: { textAlign: 'center' },
      sort: 'asc', sortIndex: 0 },
    {
      // 체결시각 — ordT 는 "yyyy-MM-dd HH:mm:ss" 문자열로 내려온다. 시각만 보여준다.
      field: 'ordT' as any, headerName: '체결시간', width: 80, flex: 0, filter: false,
      sort: 'asc', sortIndex: 1,
      valueFormatter: (p: any) => {
        if (!p.value) return '';
        const s = String(p.value);
        const i = s.indexOf(' ');
        return i > 0 ? s.slice(i + 1, i + 9) : s.slice(11, 19);
      },
      cellStyle: { textAlign: 'center', fontVariantNumeric: 'tabular-nums' },
    },
    { field: 'ordNo', headerName: '주문번호', width: 80, flex: 0, filter: false, cellStyle: { textAlign: 'right' } },
    { field: 'orgOrdNo', headerName: '원주문번호', width: 80, flex: 0, filter: false, cellStyle: { textAlign: 'right' } },
    { field: 'ordTyNm', headerName: '주문구분', width: 70, flex: 0, filter: false, cellStyle: { textAlign: 'center' } },
    {
      field: 'lsNm', headerName: '매도매수', width: 65, flex: 0, filter: false,
      cellStyle: (params) => ({ color: params.value === '매수' ? '#ef4444' : '#22c55e', fontWeight: 'bold', textAlign: 'center' })
    },
    { field: 'prodId', headerName: '종목코드', width: 75, flex: 0, filter: false,
      onCellClicked: (e: any) => {
        if (e.value) {
          setChartStock({ prodId: String(e.value), prodNm: e.data?.prodNm || String(e.value) });
          setUserPickedStock(true);
        }
      },
      cellStyle: { textAlign: 'left', cursor: 'pointer', color: '#1d4ed8', textDecoration: 'underline' } },
    { field: 'prodNm', headerName: '종목명', minWidth: 100, flex: 1, filter: false, cellStyle: { textAlign: 'left' } },
    {
      field: 'ordQty', headerName: '주문수량', width: 75, flex: 0, filter: false,
      valueFormatter: (params) => params.value ? params.value.toLocaleString() : '0',
      cellStyle: { textAlign: 'right' }
    },
    {
      field: 'ordPrc', headerName: '주문단가', width: 85, flex: 0, filter: false,
      valueFormatter: (params) => params.value ? params.value.toLocaleString() : '0',
      cellStyle: { textAlign: 'right' }
    },
    {
      field: 'ordT', headerName: '주문시각', width: 145, flex: 0, filter: false,
      cellStyle: { textAlign: 'center' }
    },
    {
      field: 'totFillQty', headerName: '체결수량', width: 75, flex: 0, filter: false,
      valueFormatter: (params) => params.value ? params.value.toLocaleString() : '0',
      cellStyle: { textAlign: 'right', fontWeight: 'bold' }
    },
    {
      field: 'rmnQty', headerName: '미체결', width: 65, flex: 0, filter: false,
      valueFormatter: (params) => params.value ? params.value.toLocaleString() : '0',
      cellStyle: (params) => ({ textAlign: 'right', fontWeight: 'bold', color: params.value > 0 ? '#f59e0b' : '#22c55e' })
    },
    {
      field: 'avgPrc', headerName: '평균가', width: 85, flex: 0, filter: false,
      valueFormatter: (params) => params.value ? Math.round(params.value).toLocaleString() : '0',
      cellStyle: { textAlign: 'right' }
    },
    {
      field: 'totFillAmt', headerName: '체결금액', width: 110, flex: 0, filter: false,
      valueFormatter: (params) => params.value ? params.value.toLocaleString() : '0',
      cellStyle: { textAlign: 'right', fontWeight: 'bold' }
    },
    {
      // 수수료·거래세 — 서버가 주는 값만 표시한다. 역산하지 않는다.
      //   KIS 체결API(inquire_daily_ccld)는 종목별 수수료/세금을 분리해 주지 않고
      //   prsm_tlex_smtl(추정제비용합계)만 있다. 그래서 KIS 계좌는 보통 빈칸(-)이다.
      //   sim 계정은 SimExchangeService 가 실제 계산값을 채운다.
      field: 'fee' as any, headerName: '수수료', width: 70, flex: 0, filter: false,
      valueFormatter: (p) => p.value == null ? '-' : Number(p.value).toLocaleString(),
      cellStyle: { textAlign: 'right', color: '#6b7280' },
    },
    {
      field: 'tax' as any, headerName: '거래세', width: 70, flex: 0, filter: false,
      valueFormatter: (p) => p.value == null ? '-' : Number(p.value).toLocaleString(),
      cellStyle: { textAlign: 'right', color: '#6b7280' },
    },
    {
      field: 'cnclYn', headerName: '취소', width: 45, flex: 0, filter: false,
      cellStyle: (params) => ({ color: params.value === 'Y' ? '#ef4444' : '#22c55e', fontWeight: 'bold', textAlign: 'center' })
    },
    {
      field: 'cnclQty', headerName: '취소수량', width: 70, flex: 0, filter: false,
      valueFormatter: (params) => params.value ? params.value.toLocaleString() : '0',
      cellStyle: { textAlign: 'right' }
    },
    {
      field: 'rjctQty', headerName: '거부수량', width: 70, flex: 0, filter: false,
      valueFormatter: (params) => params.value ? params.value.toLocaleString() : '0',
      cellStyle: { textAlign: 'right' }
    },
  ], []);

  // 슬라이스 컬럼 정의
  const sliceColumnDefs = useMemo<ColDef<KisPfSlice>[]>(() => [
    { field: 'id', headerName: 'ID', width: 50, flex: 0, filter: false, cellStyle: { textAlign: 'right' } },
    { field: 'ordId', headerName: '주문ID', width: 65, flex: 0, filter: false, cellStyle: { textAlign: 'right' } },
    { field: 'prodId', headerName: '종목', width: 70, flex: 0, filter: false, cellStyle: { textAlign: 'center' },
      valueFormatter: (p: any) => p.value ?? '' },
    { field: 'ex', headerName: '구분', width: 55, flex: 0, filter: false,
      valueFormatter: (p: any) => p.value === 1 ? '매수' : p.value === -1 ? '매도' : '',
      cellStyle: (p: any) => ({ textAlign: 'center', color: p.value === 1 ? '#dc2626' : '#2563eb' }) },
    {
      field: 't', headerName: '생성시각', width: 155, flex: 0, filter: false,
      valueFormatter: (params) => {
        if (!params.value) return '';
        try {
          const d = new Date(params.value);
          return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')} ${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}:${String(d.getSeconds()).padStart(2,'0')}`;
        } catch { return params.value; }
      },
      cellStyle: { textAlign: 'center' }
    },
    {
      field: 'qty2ord', headerName: '주문수량', width: 75, flex: 0, filter: false,
      valueFormatter: (params) => params.value ? params.value.toLocaleString() : '0',
      cellStyle: { textAlign: 'right' }
    },
    {
      field: 'fillCumQty', headerName: '체결수량', width: 75, flex: 0, filter: false,
      valueFormatter: (params) => params.value ? params.value.toLocaleString() : '0',
      cellStyle: { textAlign: 'right', fontWeight: 'bold' }
    },
    {
      headerName: '미체결', width: 70, flex: 0, filter: false,
      valueGetter: (params) => {
        const qty = params.data?.qty2ord ?? 0;
        const fill = params.data?.fillCumQty ?? 0;
        return qty - fill;
      },
      valueFormatter: (params) => params.value != null ? params.value.toLocaleString() : '0',
      cellStyle: (params) => ({ textAlign: 'right', fontWeight: 'bold', color: params.value > 0 ? '#f59e0b' : '#22c55e' }),
    },
    {
      field: 'limitPrc', headerName: '지정가', width: 85, flex: 0, filter: false,
      valueFormatter: (params) => params.value ? Math.round(params.value).toLocaleString() : '',
      cellStyle: { textAlign: 'right' }
    },
    { field: 'ordNo', headerName: '주문번호', width: 95, flex: 0, filter: false, cellStyle: { textAlign: 'right' } },
    { field: 'ordAcceptT', headerName: '접수', width: 80, flex: 0, filter: false, cellStyle: { textAlign: 'center' } },
    { field: 'krxOrgNo', headerName: '거래소번호', width: 110, flex: 0, filter: false, cellStyle: { textAlign: 'center' } },
    {
      field: 'cnclT', headerName: '취소시각', width: 155, flex: 0, filter: false,
      valueFormatter: (params) => {
        if (!params.value) return '';
        try {
          const d = new Date(params.value);
          return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')} ${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}:${String(d.getSeconds()).padStart(2,'0')}`;
        } catch { return params.value; }
      },
      cellStyle: (params) => ({ color: params.value ? '#ef4444' : 'inherit', textAlign: 'center' })
    },
    {
      field: 'done', headerName: '완료', width: 50, flex: 0, filter: false,
      valueFormatter: (params) => params.value ? 'Y' : 'N',
      cellStyle: (params) => ({ color: params.value ? '#22c55e' : '#f59e0b', fontWeight: 'bold', textAlign: 'center' })
    },
    { field: 'msg', headerName: '생성내역', flex: 1, minWidth: 250, filter: false, cellStyle: { textAlign: 'left', fontSize: '11px', color: '#6b7280' } },
    {
      field: 'errMsg', headerName: '에러', width: 150, flex: 0, filter: false,
      cellStyle: (params) => ({ color: params.value ? '#ef4444' : 'inherit', textAlign: 'left' })
    },
  ], []);

  /**
   * 선택 종목의 주문·체결을 차트 표식으로 변환한다.
   *
   * 체결(kispffill)은 실제 체결가(avgPrc)·체결시각(ordT)이 있어 그대로 쓴다.
   * 주문(kispforder)은 **체결되지 않은 것만** 올린다 — 체결된 주문은 체결 표식과
   * 같은 자리에 겹쳐 찍혀 화면만 지저분해진다.
   *
   * 매수/매도 판정은 이 시스템 규칙대로 ls×ex 로 한다 (rating-map: sgn>0 매수).
   */
  /** 차트 종목 드롭다운 목록 — 그날 주문·체결이 있던 종목만. 거래 많은 순. */
  const chartStockOptions = useMemo(() => {
    // **차트에 실제로 찍힐 표식 수**로 정렬한다 (2026-10-06).
    //
    // 예전에는 주문+체결 "행 수" 로 정렬해서, 전량 거절된 종목이 1순위로
    // 뽑혔다. 거절 주문은 체결가도 지정가도 없어 표식을 못 찍는다 →
    // 차트가 비어 "표식 19건인데 아무것도 안 보인다" 가 됐다
    // (실측 10-02: 114800 주문 19건·체결 0건·limitPrc 전부 null).
    //
    // 체결(가격 있음)은 1점, 미체결 주문은 지정가가 있을 때만 1점으로 센다.
    const cnt = new Map<string, { n: number; nm: string }>();
    const add = (id: string, nm: string | undefined, plottable: boolean) => {
      if (!id) return;
      const cur = cnt.get(id) ?? { n: 0, nm: nm || id };
      cnt.set(id, { n: cur.n + (plottable ? 1 : 0), nm: cur.nm });
    };
    fills.forEach(f => add(f.prodId, (f as any).prodNm, (f.avgPrc ?? 0) > 0));
    orders.forEach(o => add(o.prodId, o.prodNm,
      (o.cumQty ?? 0) === 0 && ((o as any).limitPrc ?? 0) > 0));

    return [...cnt.entries()]
      .sort((a, b) => b[1].n - a[1].n)
      .map(([prodId, v]) => ({ prodId, prodNm: v.nm, drawable: v.n }));
  }, [orders, fills]);

  // 조회일자·계좌가 바뀌면 사용자의 종목 선택을 푼다 — 그 종목은 새 조건에
  // 거래가 없을 수 있다. 풀어야 자동선택이 그날 종목으로 다시 잡는다.
  // 펼쳐둔 체결손익 세부도 함께 닫는다 — 다른 날 내용이 남아 있으면 오해를 부른다.
  useEffect(() => {
    setUserPickedStock(false);
    setChartStock(null);
    setTradeDetail(null);
    setTradeDetailD(null);
  }, [selectedDate, apiPfId]);

  /**
   * 차트 종목 자동 선택.
   *
   * 예전에는 종목코드를 **클릭해야만** 차트가 열렸다. 그런데 차트가 주문내역
   * 아래(약 810px)에 있어 클릭해도 화면이 안 움직이니 "안 나온다" 로 보였다.
   * (2026-10-03 사용자 지적)
   *
   * 이제 주문·체결이 로드되면 **가장 많이 거래된 종목을 자동으로 띄운다.**
   * 클릭은 여전히 동작한다 — 다른 종목으로 바꿀 때 쓴다.
   */
  useEffect(() => {
    if (chartStockOptions.length === 0) return;   // 그날 거래가 없으면 띄울 것도 없다
    // 조회일자/계좌가 바뀌면 그 전 선택은 무효다 — 그 종목은 새 날짜에 거래가
    // 없을 수 있다. 예전에는 `if (chartStock) return` 로 무조건 유지해서,
    // 10-06 에서 10-02 로 날짜를 바꿔도 차트가 10-06 종목에 그대로 머물렀다
    // (2026-10-06 브라우저 실측).
    //
    // **사용자가 직접 고른 종목은 무조건 존중한다** — 표식이 하나도 없는
    // 종목(전량 거절)이라도 그대로 둔다. 여기서 되돌리면 드롭다운이
    // 제자리로 튕겨 "선택이 안 된다" 가 된다 (2026-10-06 실측).
    if (userPickedStock) return;
    //
    // 자동 선택일 때만 **표식을 찍을 수 있는 종목**을 고른다. 거절만 있는
    // 종목(drawable=0)이 뽑히면 차트가 텅 빈 채로 "표식 19건" 이라 적혔다.
    const keep = chartStock && chartStockOptions.find(o => o.prodId === chartStock.prodId);
    if (keep && keep.drawable > 0) return;
    setChartStock(chartStockOptions[0]);          // 표식이 가장 많은 종목
  }, [chartStockOptions, chartStock, userPickedStock]);

  const chartMarkers = useMemo<TradeMarker[]>(() => {
    if (!chartStock) return [];
    const pid = chartStock.prodId;
    const out: TradeMarker[] = [];

    fills.filter(f => f.prodId === pid).forEach(f => {
      out.push({
        t: f.ordT,
        prc: f.avgPrc,
        qty: f.totFillQty,
        // kispffill.ls 는 문자열이다 — '01'=매도, '02'=매수 (DB 실측 2026-09-29)
        isBuy: String(f.ls) === '02',
        isFill: true,
        note: f.ordTyNm || undefined,
      });
    });

    orders.filter(o => o.prodId === pid && (o.cumQty ?? 0) === 0).forEach(o => {
      out.push({
        // **t0 를 먼저 쓴다** — t0 는 항상 KST 장 시각이고, genT 는 생성 시각이라
        // 경로에 따라 UTC 로 저장된 과거 데이터가 남아 있다(9시간 밀려 찍힌다).
        // 생성 코드는 2026-10-07 에 KST 로 통일했지만, 그 전 주문은 그대로다.
        t: o.t0 || o.genT,
        // 미체결이라 체결가가 없다. 지정가 → 없으면 표식을 건너뛴다(가격축 위치 불명)
        prc: (o as any).limitPrc ?? null,
        qty: o.qty2Ord,
        isBuy: (o.ls ?? 0) * (o.ex ?? 0) > 0,
        isFill: false,
        note: (o as any).errMsg || (o as any).msg || undefined,
      });
    });

    return out;
  }, [chartStock, orders, fills]);

  /**
   * 표식 개수를 **그려지는 것만** 센다 (2026-10-06).
   *
   * StockChart 는 가격이 없는 표식(거절 주문 등)을 버린다 — 가격축에 놓을
   * 자리가 없다. 그런데 헤더는 버려진 것까지 세서 "표식 19건" 이라 적었고,
   * 차트는 텅 비어 있었다. 숫자와 화면이 어긋나면 디버깅이 불가능하다.
   */
  const chartMarkerStat = useMemo(() => {
    const drawn = chartMarkers.filter(m => m.prc != null && m.prc > 0).length;
    return { drawn, skipped: chartMarkers.length - drawn };
  }, [chartMarkers]);

  const defaultColDef = useMemo<ColDef>(() => {
    return {
      flex: 1,
      minWidth: 30,
      resizable: true,
      sortable: true,
      filter: false,
      floatingFilter: false,
      suppressHeaderMenuButton: true,
      // 더블클릭 시 셀 값 클립보드 복사
      onCellDoubleClicked: (e: any) => {
        const val = e.value;
        if (val != null) {
          const text = String(val);
          navigator.clipboard?.writeText(text).then(
            () => console.log(`복사: ${text}`),
            () => {
              const ta = document.createElement('textarea');
              ta.value = text; ta.style.position = 'fixed'; ta.style.left = '-9999px';
              document.body.appendChild(ta); ta.select();
              document.execCommand('copy'); document.body.removeChild(ta);
            }
          );
        }
      },
      cellStyle: { fontSize: '11px' },
      headerStyle: {
        fontSize: '11px',
        fontWeight: '600',
        textAlign: 'center',
        padding: '8px',
        backgroundColor: '#f3f4f6',
        borderBottom: '2px solid #e5e7eb'
      }
    };
  }, []);

  // 포지션 요약 정보 계산
  const positionSummary = useMemo(() => {
    const totalQty = positions.reduce((sum, pos) => sum + (pos.pos || pos.qty || 0), 0);
    const totalPrft = positions.reduce((sum, pos) => sum + (pos.prft || 0), 0);
    const totalValue = positions.reduce((sum, pos) => sum + (pos.evluamt || ((pos.c || pos.currPx || 0) * (pos.pos || pos.qty || 0))), 0);
    const totalBamt = positions.reduce((sum, pos) => sum + (pos.bamt || 0), 0);
    // 수익률 = (평가액 - 매입액) / 매입액 * 100
    const totalWei = totalBamt > 0 ? ((totalValue - totalBamt) / totalBamt * 100) : 0;

    return {
      totalQty,
      totalWei,
      totalPrft,
      totalValue,
      totalBamt,
      positionCount: positions.length
    };
  }, [positions]);

  if (loading) {
    return (
      <div style={{ padding: '20px', backgroundColor: '#eff6ff', minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <div style={{ color: '#1e40af', fontSize: '12px', fontWeight: '500' }}>데이터 로드 중...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '20px', backgroundColor: '#eff6ff', minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <div style={{ color: '#ef4444', fontSize: '12px', fontWeight: '500', backgroundColor: 'white', padding: '16px', borderRadius: '8px', border: '2px solid #ef4444' }}>{error}</div>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', backgroundColor: '#eff6ff', minHeight: '100vh' }}>
      {/* 날짜 선택 및 신규 주문 */}
      <div style={{ backgroundColor: 'white', border: '2px solid #3b82f6', borderRadius: '8px', padding: '16px', marginBottom: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <label htmlFor="portfolio-date-picker" style={{ fontWeight: 'bold', color: '#1e40af', fontSize: '12px', whiteSpace: 'nowrap' }}>조회일자:</label>
              <DateInput
                id="portfolio-date-picker"
                value={selectedDate}
                onChange={(v) => setSelectedDate(v)}
                style={{ border: '2px solid #3b82f6' }}
              />
            </div>
              <div style={{ display: 'flex', gap: '8px' }}>
              {pfCanOrder && (
              <button
                onClick={() => {
                  if (portfolioId === 0) { alert('주문은 개별 계좌를 선택해야 합니다.'); return; }
                  setSelectedOrder(null);
                  setFormData({
                    date: selectedDate,
                    prodId: '',
                    prodNm: '',
                    isBuy: true,
                    wei: 0,
                    qty: null,
                    msg: '',
                    exitAll: false,
                    prcTy: 'Opp',
                    oppLmtIdx: 1,
                    myLmtIdx: 1,
                    limitPrc: null,
                    stopPrc: null,
                    isSlice: false,
                    hm0: getSliceStartTime(selectedDate),
                    hm1: '15:20',
                    nSlices: null,
                  });
                  setErrors({});
                  setStockSearchTerm('');
                  setStockSearchResults([]);
                  setShowStockSearch(false);
                  setShowOrderForm(true);
                }}
                style={{
                  padding: '5px 12px',
                  backgroundColor: portfolioId === 0 ? '#9ca3af' : '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '3px',
                  cursor: portfolioId === 0 ? 'not-allowed' : 'pointer',
                  fontSize: '12px',
                  fontWeight: '500'
                }}
              >
                신규 주문
              </button>
              )}
              <button
                onClick={() => {
                  loadOrders();
                  fetchPositions(apiPfId, selectedDate);
                  fetchRecentBalance(balancePfId, selectedDate);
                  fetchFills(apiPfId, selectedDate, selectedDate);
                  fetchPnl(apiPfId, selectedDate);
                }}
                style={{
                  padding: '5px 12px',
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '3px',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: '500'
                }}
              >
                조회
              </button>
              <button
                onClick={() => {
                  updatePosition();
                }}
                title={portfolioId === 0 ? '개별 계좌를 선택하세요' : 'KIS API에서 포지션을 다시 가져옵니다'}
                style={{
                  padding: '5px 12px',
                  backgroundColor: portfolioId === 0 ? '#9ca3af' : '#8b5cf6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '3px',
                  cursor: portfolioId === 0 ? 'not-allowed' : 'pointer',
                  fontSize: '12px',
                  fontWeight: '500'
                }}
              >
                포지션 업데이트
              </button>
              <button
                onClick={() => {
                  updatePfFill();
                }}
                style={{
                  padding: '5px 12px',
                  backgroundColor: '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '3px',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: '500'
                }}
              >
                체결 데이터 업데이트
              </button>
              {/* SIM 체결 — broker='sim' 계정 전용. selectedDate 기준이라 과거일자 테스트가 된다.
                  구 '모의 체결'(mock-fill) 버튼은 2026-08-22 제거했다.
                  가격 검증 없이 무조건 체결시켜 SIM 체결과 역할이 겹치고 혼동을 유발했다. */}
              {isSimAccount && pfCanOrder && (
                <button
                  onClick={async () => {
                    if (!confirm(
                      `가상체결거래소에서 ${selectedDate} 시세로 체결 판정합니다.\n` +
                      `지정가가 그날 고저 범위 안이면 체결됩니다. 진행할까요?`)) return;
                    try {
                      const res = await axiosInstance.post('/api/v1/pfbs/sim-fill', {}, {
                        params: { pfId: apiPfId, date: selectedDate }, timeout: 30000,
                      });
                      const r = res.data as any;
                      const lines = (r.details || [])
                        .map((x: any) => `  ${x.prodId} ${x.reason}`).join('\n');
                      alert(
                        `[SIM 체결] ${r.date}\n` +
                        `체결 ${r.filled}건 / 미체결 ${r.unfilled}건 (총 ${r.total}건)\n\n` +
                        (lines || '(대상 주문 없음)'));
                      fetchOrders(apiPfId, selectedDate, selectedDate);
                      fetchFills(apiPfId, selectedDate, selectedDate);
                      fetchPnl(apiPfId, selectedDate);
                      fetchPositions(apiPfId, selectedDate);
                    } catch (err: any) {
                      alert('SIM 체결 실패: ' + (err.response?.data?.error || err.message));
                    }
                  }}
                  title={`${selectedDate} 시세로 가상체결 판정`}
                  style={{
                    padding: '5px 12px',
                    backgroundColor: '#7c3aed',
                    color: 'white',
                    border: 'none',
                    borderRadius: '3px',
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: '500',
                  }}
                >
                  SIM 체결
                </button>
              )}
              </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#1e40af', fontSize: '12px', fontWeight: '500' }}>
            {activePfs.length > 1 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <span>계좌:</span>
                <select
                  value={portfolioId}
                  onChange={e => selectPortfolio(Number(e.target.value))}
                  style={{
                    padding: '2px 6px', fontSize: '11px', fontWeight: 500,
                    border: '1px solid #93c5fd', borderRadius: '3px',
                    backgroundColor: 'white', color: '#1e40af',
                    cursor: 'pointer', outline: 'none',
                  }}
                >
                  <option value={0}>전체</option>
                  {activePfs.map(p => (
                    <option key={p.id} value={p.id}>{pfLabel(p)}</option>
                  ))}
                </select>
                <span style={{ margin: '0 4px' }}>|</span>
              </div>
            )}
            {(() => {
              const pf = activePfs.find(p => p.id === portfolioId);
              if (!pf) return <>전체</>;
              // 계좌 상세는 위 콤보에 이미 있으므로, 콤보가 없을 때(계좌 1개)만 라벨을 표시
              return (
                <>
                  {activePfs.length <= 1 && <>계좌: {pfLabel(pf)} </>}
                  <b style={{ color: pf.isReal ? '#dc2626' : '#2563eb' }}>
                    {pf.isReal ? '실전' : '모의'}
                  </b>
                </>
              );
            })()} | 주문: {orders.length}건 | 포지션: {positions.length}개 | 체결내역: {fills.length}건
          </div>
        </div>
        {/* 에러 메시지 표시 */}
        {error && (
          <div style={{
            marginTop: '12px',
            padding: '12px',
            backgroundColor: '#fee2e2',
            border: '2px solid #ef4444',
            borderRadius: '4px',
            color: '#991b1b',
            fontSize: '12px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span>{error}</span>
            <button
              onClick={() => setError(null)}
              style={{
                background: 'none',
                border: 'none',
                color: '#991b1b',
                fontSize: '18px',
                cursor: 'pointer',
                padding: '0 8px',
                fontWeight: 'bold'
              }}
              title="닫기"
            >
              ×
            </button>
          </div>
        )}
      </div>

      {/* 신규 주문 폼 */}
      {showOrderForm && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 2000,
        }}
          onClick={() => { setShowOrderForm(false); setSelectedOrder(null); }}
        >
        <div style={{
          backgroundColor: 'white', borderRadius: '8px', padding: '20px',
          minWidth: '450px', maxWidth: '550px', width: '90%',
          maxHeight: '90vh', overflow: 'auto',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)', boxSizing: 'border-box',
        }}
          onClick={(e) => e.stopPropagation()}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h3 style={{ margin: 0, color: '#1e40af', fontSize: '12px' }}>
              {selectedOrder ? (!pfCanOrder || selectedOrder.state === 30 ? '주문 조회' : selectedOrder.state === 20 ? '주문 조회 (Working)' : '주문 정정') : '신규 주문'}
              {(() => {
                const pf = activePfs.find(p => p.id === portfolioId);
                return pf ? <span style={{ marginLeft: '8px', color: '#6b7280', fontWeight: 400 }}>({pfLabel(pf)} {pf.isReal ? '실전' : '모의'})</span> : null;
              })()}
            </h3>
            <button
              onClick={() => {
                setShowOrderForm(false);
                setSelectedOrder(null);
              }}
              style={{ color: '#6b7280', fontSize: '20px', background: 'none', border: 'none', cursor: 'pointer' }}
            >
              ×
            </button>
          </div>
          <form onSubmit={handleOrderSubmit}>
            {/* 주문 기준일 */}
            <fieldset style={{ border: '1px solid #d1d5db', borderRadius: '4px', padding: '12px', marginBottom: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <label htmlFor="order-date" style={{ fontSize: '12px', fontWeight: '500', color: '#1e40af' }}>주문 기준일:</label>
                <DateInput
                  id="order-date"
                  value={formData.date}
                  onChange={(v) => handleInputChange('date', v)}
                  disabled={selectedOrder?.state === 30 || selectedOrder?.state === 20}
                  min={new Date().toISOString().split('T')[0]}
                  style={{
                    padding: '4px 8px',
                    border: '1px solid #d1d5db',
                    backgroundColor: (selectedOrder?.state === 30 || selectedOrder?.state === 20) ? '#f3f4f6' : 'white',
                  }}
                />
                {errors.date && <div style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>{errors.date}</div>}
                {errors.hm0 && <div style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>{errors.hm0}</div>}
              </div>
              {/* 주문 슬롯 — 신규 주문만. 슬롯 단위 권한이면 필수 */}
              {!selectedOrder && (pfOrderSlots || slotOpts.length > 0) && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px' }}>
                  <label htmlFor="order-slot" style={{ fontSize: '12px', fontWeight: '500', color: '#1e40af' }}>슬롯:</label>
                  <select
                    id="order-slot"
                    value={orderSlotId ?? ''}
                    onChange={(e) => setOrderSlotId(e.target.value ? Number(e.target.value) : null)}
                    style={{ padding: '4px 8px', border: '1px solid #d1d5db', fontSize: '12px' }}
                  >
                    {!pfOrderSlots && <option value="">기본 (MANUAL)</option>}
                    {slotOpts.map(s => <option key={s.id} value={s.id}>{s.id} · {s.name}</option>)}
                  </select>
                  {pfOrderSlots && slotOpts.length === 0 && (
                    <span style={{ color: '#9ca3af', fontSize: '12px' }}>주문할 수 있는 운용 중 슬롯이 없습니다</span>
                  )}
                  {errors.slot && <span style={{ color: 'red', fontSize: '12px' }}>{errors.slot}</span>}
                </div>
              )}
            </fieldset>

            {/* 종목 선택 */}
            <fieldset style={{ border: '1px solid #d1d5db', borderRadius: '4px', padding: '12px', marginBottom: '12px' }}>
              <div style={{ marginBottom: '8px', position: 'relative' }}>
                <label htmlFor="order-prod-id" style={{ display: 'block', fontSize: '12px', fontWeight: '500', color: '#1e40af', marginBottom: '4px' }}>
                  종목코드 / 종목명:
                </label>
                <input
                  id="order-prod-id"
                  type="text"
                  value={stockSearchTerm || formData.prodId}
                  onChange={(e) => {
                    const val = e.target.value;
                    setStockSearchTerm(val);
                    handleInputChange('prodId', val);
                    if (!val) {
                      setFormData(prev => ({ ...prev, prodNm: '' }));
                      setStockSearchResults([]);
                      setShowStockSearch(false);
                    }
                  }}
                  onFocus={() => {
                    if (stockSearchResults.length > 0) setShowStockSearch(true);
                  }}
                  onBlur={() => {
                    setTimeout(() => setShowStockSearch(false), 200);
                  }}
                  disabled={selectedOrder?.state === 30 || selectedOrder?.state === 20}
                  placeholder="종목코드 또는 종목명 검색 (예: 삼성전자, 069500)"
                  style={{
                    display: 'block',
                    width: '100%',
                    padding: '8px',
                    border: errors.prodId ? '1px solid #ef4444' : '1px solid #d1d5db',
                    borderRadius: '4px',
                    fontSize: '12px',
                    backgroundColor: (selectedOrder?.state === 30 || selectedOrder?.state === 20) ? '#f3f4f6' : 'white',
                    cursor: (selectedOrder?.state === 30 || selectedOrder?.state === 20) ? 'not-allowed' : 'text',
                    boxSizing: 'border-box',
                  }}
                />
                {/* 종목 검색 결과 드롭다운 */}
                {showStockSearch && (
                  <div style={{
                    position: 'absolute', top: '100%', left: 0, right: 0, marginTop: '4px',
                    backgroundColor: 'white', border: '1px solid #d1d5db', borderRadius: '4px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)', maxHeight: '200px', overflowY: 'auto', zIndex: 3000
                  }}>
                    {stockSearchResults.length > 0 ? stockSearchResults.map((result, idx) => (
                      <div
                        key={idx}
                        onMouseDown={(e) => { e.preventDefault(); handleSelectStock(result); }}
                        style={{
                          padding: '8px 12px', cursor: 'pointer',
                          borderBottom: idx < stockSearchResults.length - 1 ? '1px solid #e5e7eb' : 'none',
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#eff6ff')}
                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'white')}
                      >
                        <div style={{ fontSize: '12px', fontWeight: '600', color: '#1e40af' }}>{result.prodNm}</div>
                        <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px' }}>{result.prodId} ({result.market})</div>
                      </div>
                    )) : (
                      <div style={{ padding: '12px', textAlign: 'center', color: '#6b7280', fontSize: '12px' }}>검색 결과가 없습니다.</div>
                    )}
                  </div>
                )}
                {errors.prodId && <div style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>{errors.prodId}</div>}
                {formData.prodId && formData.prodNm && !stockSearchTerm && (
                  <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '8px' }}>
                    {formData.prodNm} ({formData.prodId})
                  </div>
                )}
              </div>
            </fieldset>

            {/* 매수/매도 및 주문 비중 */}
            <fieldset style={{ border: '1px solid #d1d5db', borderRadius: '4px', padding: '12px', marginBottom: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '8px' }}>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    type="button"
                    onClick={() => handleInputChange('isBuy', true)}
                    disabled={selectedOrder?.state === 30 || selectedOrder?.state === 20}
                    style={{
                      padding: '5px 12px',
                      backgroundColor: formData.isBuy ? '#3b82f6' : '#e5e7eb',
                      color: formData.isBuy ? 'white' : '#374151',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: (selectedOrder?.state === 30 || selectedOrder?.state === 20) ? 'not-allowed' : 'pointer',
                      fontSize: '12px',
                      opacity: (selectedOrder?.state === 30 || selectedOrder?.state === 20) ? 0.6 : 1
                    }}
                  >
                    매수
                  </button>
                  <button
                    type="button"
                    onClick={() => handleInputChange('isBuy', false)}
                    disabled={selectedOrder?.state === 30 || selectedOrder?.state === 20}
                    style={{
                      padding: '5px 12px',
                      backgroundColor: !formData.isBuy ? '#3b82f6' : '#e5e7eb',
                      color: !formData.isBuy ? 'white' : '#374151',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: (selectedOrder?.state === 30 || selectedOrder?.state === 20) ? 'not-allowed' : 'pointer',
                      fontSize: '12px',
                      opacity: (selectedOrder?.state === 30 || selectedOrder?.state === 20) ? 0.6 : 1
                    }}
                  >
                    매도
                  </button>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <label htmlFor="order-wei" style={{ fontSize: '12px', color: '#1e40af' }}>비중(%):</label>
                  <input
                    id="order-wei"
                    type="number"
                    value={formData.wei === 0 ? '' : formData.wei}
                    onChange={(e) => {
                      const value = e.target.value === '' ? 0 : parseFloat(e.target.value) || 0;
                      handleInputChange('wei', value);
                    }}
                    onFocus={(e) => {
                      if (e.target.value === '0' || e.target.value === '') {
                        e.target.select();
                      }
                    }}
                    min="0"
                    max="100"
                    step="0.01"
                    placeholder="0"
                    disabled={formData.exitAll || (formData.qty != null && formData.qty > 0) || selectedOrder?.state === 30 || selectedOrder?.state === 20}
                    style={{
                      width: '80px',
                      padding: '4px 8px',
                      border: '1px solid #d1d5db',
                      borderRadius: '4px',
                      fontSize: '12px',
                      backgroundColor: (selectedOrder?.state === 30 || selectedOrder?.state === 20) ? '#f3f4f6' : 'white',
                      cursor: (selectedOrder?.state === 30 || selectedOrder?.state === 20) ? 'not-allowed' : 'text'
                    }}
                  />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <label htmlFor="order-qty" style={{ fontSize: '12px', color: '#1e40af' }}>수량:</label>
                  <input
                    id="order-qty"
                    type="number"
                    value={formData.qty == null ? '' : formData.qty}
                    onChange={(e) => {
                      const value = e.target.value === '' ? null : parseInt(e.target.value) || 0;
                      handleInputChange('qty', value);
                      if (value != null && value > 0) {
                        handleInputChange('wei', 0);
                      }
                    }}
                    onFocus={(e) => {
                      if (e.target.value === '0' || e.target.value === '') {
                        e.target.select();
                      }
                    }}
                    min="0"
                    step="1"
                    placeholder="0"
                    disabled={formData.exitAll || selectedOrder?.state === 30 || selectedOrder?.state === 20}
                    style={{
                      width: '80px',
                      padding: '4px 8px',
                      border: '1px solid #d1d5db',
                      borderRadius: '4px',
                      fontSize: '12px',
                      backgroundColor: (selectedOrder?.state === 30 || selectedOrder?.state === 20) ? '#f3f4f6' : 'white',
                      cursor: (selectedOrder?.state === 30 || selectedOrder?.state === 20) ? 'not-allowed' : 'text'
                    }}
                  />
                </div>
              </div>
              {!formData.isBuy && (
                <div style={{ marginTop: '8px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px' }}>
                    <input
                      type="checkbox"
                      checked={formData.exitAll}
                      onChange={(e) => {
                        handleInputChange('exitAll', e.target.checked);
                        if (e.target.checked) {
                          handleInputChange('wei', 0);
                        }
                      }}
                      disabled={selectedOrder?.state === 30 || selectedOrder?.state === 20}
                    />
                    전량 청산
                  </label>
                </div>
              )}
              {errors.wei && <div style={{ color: 'red', fontSize: '12px', marginTop: '8px' }}>{errors.wei}</div>}
            </fieldset>

            {/* 가격 타입 */}
            <fieldset style={{ border: '1px solid #d1d5db', borderRadius: '4px', padding: '12px', marginBottom: '12px' }}>
              <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                <button
                  type="button"
                  onClick={() => handleInputChange('prcTy', 'Opp')}
                  disabled={selectedOrder?.state === 30 || selectedOrder?.state === 20}
                  style={{
                    padding: '5px 12px',
                    backgroundColor: formData.prcTy === 'Opp' ? '#3b82f6' : '#e5e7eb',
                    color: formData.prcTy === 'Opp' ? 'white' : '#374151',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: (selectedOrder?.state === 30 || selectedOrder?.state === 20) ? 'not-allowed' : 'pointer',
                    fontSize: '12px',
                    opacity: (selectedOrder?.state === 30 || selectedOrder?.state === 20) ? 0.6 : 1
                  }}
                >
                  상대호가
                </button>
                <button
                  type="button"
                  onClick={() => handleInputChange('prcTy', 'My')}
                  disabled={selectedOrder?.state === 30 || selectedOrder?.state === 20}
                  style={{
                    padding: '5px 12px',
                    backgroundColor: formData.prcTy === 'My' ? '#3b82f6' : '#e5e7eb',
                    color: formData.prcTy === 'My' ? 'white' : '#374151',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: (selectedOrder?.state === 30 || selectedOrder?.state === 20) ? 'not-allowed' : 'pointer',
                    fontSize: '12px',
                    opacity: (selectedOrder?.state === 30 || selectedOrder?.state === 20) ? 0.6 : 1
                  }}
                >
                  자기호가
                </button>
                <button
                  type="button"
                  onClick={() => handleInputChange('prcTy', 'Limit')}
                  disabled={selectedOrder?.state === 30 || selectedOrder?.state === 20}
                  style={{
                    padding: '5px 12px',
                    backgroundColor: formData.prcTy === 'Limit' ? '#3b82f6' : '#e5e7eb',
                    color: formData.prcTy === 'Limit' ? 'white' : '#374151',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: (selectedOrder?.state === 30 || selectedOrder?.state === 20) ? 'not-allowed' : 'pointer',
                    fontSize: '12px',
                    opacity: (selectedOrder?.state === 30 || selectedOrder?.state === 20) ? 0.6 : 1
                  }}
                >
                  지정가
                </button>
                <button
                  type="button"
                  onClick={() => handleInputChange('prcTy', 'Stop')}
                  disabled={selectedOrder?.state === 30 || selectedOrder?.state === 20}
                  style={{
                    padding: '5px 12px',
                    backgroundColor: formData.prcTy === 'Stop' ? '#3b82f6' : '#e5e7eb',
                    color: formData.prcTy === 'Stop' ? 'white' : '#374151',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: (selectedOrder?.state === 30 || selectedOrder?.state === 20) ? 'not-allowed' : 'pointer',
                    fontSize: '12px',
                    opacity: (selectedOrder?.state === 30 || selectedOrder?.state === 20) ? 0.6 : 1
                  }}
                >
                  STOP
                </button>
              </div>

              {/* 호가 설정 */}
              {formData.prcTy === 'Opp' && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <label htmlFor="order-opp-lmt-idx" style={{ fontSize: '12px', color: '#1e40af' }}>상대호가 (1~10):</label>
                  <input
                    id="order-opp-lmt-idx"
                    type="number"
                    value={formData.oppLmtIdx}
                    onChange={(e) => handleInputChange('oppLmtIdx', parseInt(e.target.value) || 0)}
                    min="1"
                    max="10"
                    disabled={selectedOrder?.state === 30 || selectedOrder?.state === 20}
                    style={{
                      width: '70px',
                      padding: '4px 8px',
                      border: '1px solid #d1d5db',
                      borderRadius: '4px',
                      fontSize: '12px',
                      backgroundColor: (selectedOrder?.state === 30 || selectedOrder?.state === 20) ? '#f3f4f6' : 'white',
                      cursor: (selectedOrder?.state === 30 || selectedOrder?.state === 20) ? 'not-allowed' : 'text'
                    }}
                  />
                  {errors.oppLmtIdx && <span style={{ color: 'red', fontSize: '12px' }}>{errors.oppLmtIdx}</span>}
                </div>
              )}

              {formData.prcTy === 'My' && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <label htmlFor="order-my-lmt-idx" style={{ fontSize: '12px', color: '#1e40af' }}>자기호가 (1~10):</label>
                  <input
                    id="order-my-lmt-idx"
                    type="number"
                    value={formData.myLmtIdx}
                    onChange={(e) => handleInputChange('myLmtIdx', parseInt(e.target.value) || 0)}
                    min="1"
                    max="10"
                    disabled={selectedOrder?.state === 30 || selectedOrder?.state === 20}
                    style={{
                      width: '70px',
                      padding: '4px 8px',
                      border: '1px solid #d1d5db',
                      borderRadius: '4px',
                      fontSize: '12px',
                      backgroundColor: (selectedOrder?.state === 30 || selectedOrder?.state === 20) ? '#f3f4f6' : 'white',
                      cursor: (selectedOrder?.state === 30 || selectedOrder?.state === 20) ? 'not-allowed' : 'text'
                    }}
                  />
                  {errors.myLmtIdx && <span style={{ color: 'red', fontSize: '12px' }}>{errors.myLmtIdx}</span>}
                </div>
              )}

              {formData.prcTy === 'Limit' && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <label htmlFor="order-limit-prc" style={{ fontSize: '12px', color: '#1e40af' }}>지정가:</label>
                  <input
                    id="order-limit-prc"
                    type="number"
                    value={formData.limitPrc || ''}
                    onChange={(e) => handleInputChange('limitPrc', parseFloat(e.target.value) || null)}
                    disabled={selectedOrder?.state === 30 || selectedOrder?.state === 20}
                    style={{
                      width: '140px',
                      padding: '4px 8px',
                      border: '1px solid #d1d5db',
                      borderRadius: '4px',
                      fontSize: '12px',
                      backgroundColor: (selectedOrder?.state === 30 || selectedOrder?.state === 20) ? '#f3f4f6' : 'white',
                      cursor: (selectedOrder?.state === 30 || selectedOrder?.state === 20) ? 'not-allowed' : 'text'
                    }}
                  />
                  {errors.limitPrc && <span style={{ color: 'red', fontSize: '12px' }}>{errors.limitPrc}</span>}
                </div>
              )}

              {formData.prcTy === 'Stop' && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <label htmlFor="order-stop-prc" style={{ fontSize: '12px', color: '#1e40af' }}>STOP 가격:</label>
                  <input
                    id="order-stop-prc"
                    type="number"
                    value={formData.stopPrc || ''}
                    onChange={(e) => handleInputChange('stopPrc', parseFloat(e.target.value) || null)}
                    disabled={selectedOrder?.state === 30 || selectedOrder?.state === 20}
                    style={{
                      width: '140px',
                      padding: '4px 8px',
                      border: '1px solid #d1d5db',
                      borderRadius: '4px',
                      fontSize: '12px',
                      backgroundColor: (selectedOrder?.state === 30 || selectedOrder?.state === 20) ? '#f3f4f6' : 'white',
                      cursor: (selectedOrder?.state === 30 || selectedOrder?.state === 20) ? 'not-allowed' : 'text'
                    }}
                  />
                  {errors.stopPrc && <span style={{ color: 'red', fontSize: '12px' }}>{errors.stopPrc}</span>}
                </div>
              )}
            </fieldset>

            {/* 실행 타입 및 시간 */}
            <fieldset style={{ border: '1px solid #d1d5db', borderRadius: '4px', padding: '12px', marginBottom: '12px' }}>
              <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                <button
                  type="button"
                  onClick={() => handleInputChange('isSlice', false)}
                  disabled={selectedOrder?.state === 30 || selectedOrder?.state === 20}
                  style={{
                    padding: '5px 12px',
                    backgroundColor: !formData.isSlice ? '#3b82f6' : '#e5e7eb',
                    color: !formData.isSlice ? 'white' : '#374151',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: (selectedOrder?.state === 30 || selectedOrder?.state === 20) ? 'not-allowed' : 'pointer',
                    fontSize: '12px',
                    opacity: (selectedOrder?.state === 30 || selectedOrder?.state === 20) ? 0.6 : 1
                  }}
                >
                  즉시 실행
                </button>
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, isSlice: true, hm0: getSliceStartTime(prev.date) }))}
                  disabled={selectedOrder?.state === 30 || selectedOrder?.state === 20}
                  style={{
                    padding: '5px 12px',
                    backgroundColor: formData.isSlice ? '#3b82f6' : '#e5e7eb',
                    color: formData.isSlice ? 'white' : '#374151',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: (selectedOrder?.state === 30 || selectedOrder?.state === 20) ? 'not-allowed' : 'pointer',
                    fontSize: '12px',
                    opacity: (selectedOrder?.state === 30 || selectedOrder?.state === 20) ? 0.6 : 1
                  }}
                >
                  시간 분할
                </button>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <label htmlFor="order-hm0" style={{ fontSize: '12px', color: '#1e40af' }}>주문 시작 (한국 시간):</label>
                <input
                  id="order-hm0"
                  type="text"
                  value={formData.hm0}
                  onChange={(e) => handleInputChange('hm0', e.target.value)}
                  disabled={selectedOrder?.state === 30 || selectedOrder?.state === 20}
                  style={{
                    width: '100px',
                    padding: '4px 8px',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    fontSize: '12px',
                    backgroundColor: (selectedOrder?.state === 30 || selectedOrder?.state === 20) ? '#f3f4f6' : 'white',
                    cursor: (selectedOrder?.state === 30 || selectedOrder?.state === 20) ? 'not-allowed' : 'text'
                  }}
                  placeholder="09:00"
                />
              </div>

              {formData.isSlice && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <label htmlFor="order-hm1" style={{ fontSize: '12px', color: '#1e40af' }}>주문 종료 (한국 시간):</label>
                  <input
                    id="order-hm1"
                    type="text"
                    value={formData.hm1}
                    onChange={(e) => handleInputChange('hm1', e.target.value)}
                    disabled={selectedOrder?.state === 30 || selectedOrder?.state === 20}
                    style={{
                      width: '100px',
                      padding: '4px 8px',
                      border: '1px solid #d1d5db',
                      borderRadius: '4px',
                      fontSize: '12px',
                      backgroundColor: (selectedOrder?.state === 30 || selectedOrder?.state === 20) ? '#f3f4f6' : 'white',
                      cursor: (selectedOrder?.state === 30 || selectedOrder?.state === 20) ? 'not-allowed' : 'text'
                    }}
                    placeholder="15:20"
                  />
                  {errors.hm1 && <span style={{ color: 'red', fontSize: '12px' }}>{errors.hm1}</span>}
                </div>
              )}

              {formData.isSlice && formData.hm1 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <label htmlFor="order-nSlices" style={{ fontSize: '12px', color: '#1e40af' }}>분할 횟수:</label>
                  <input
                    id="order-nSlices"
                    type="number"
                    min={2}
                    max={100}
                    value={formData.nSlices ?? ''}
                    onChange={(e) => handleInputChange('nSlices', e.target.value ? parseInt(e.target.value) : null)}
                    disabled={selectedOrder?.state === 30 || selectedOrder?.state === 20}
                    style={{
                      width: '100px',
                      padding: '4px 8px',
                      border: '1px solid #d1d5db',
                      borderRadius: '4px',
                      fontSize: '12px',
                      backgroundColor: (selectedOrder?.state === 30 || selectedOrder?.state === 20) ? '#f3f4f6' : 'white',
                      cursor: (selectedOrder?.state === 30 || selectedOrder?.state === 20) ? 'not-allowed' : 'text'
                    }}
                    placeholder="미입력 시 자동"
                  />
                </div>
              )}
            </fieldset>

            {/* 코멘트 */}
            <div style={{ marginBottom: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <label htmlFor="order-msg" style={{ fontSize: '12px', color: '#1e40af', whiteSpace: 'nowrap' }}>코멘트:</label>
                <input
                  id="order-msg"
                  type="text"
                  value={formData.msg}
                  onChange={(e) => handleInputChange('msg', e.target.value)}
                  disabled={selectedOrder?.state === 30 || selectedOrder?.state === 20}
                  placeholder="주문 메모"
                  style={{
                    flex: 1,
                    padding: '4px 8px',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    fontSize: '12px',
                    backgroundColor: (selectedOrder?.state === 30 || selectedOrder?.state === 20) ? '#f3f4f6' : 'white',
                    cursor: (selectedOrder?.state === 30 || selectedOrder?.state === 20) ? 'not-allowed' : 'text'
                  }}
                />
              </div>
            </div>

            {/* 버튼 */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '8px' }}>
              {pfCanOrder && selectedOrder && selectedOrder.state !== 30 && selectedOrder.state !== 20 && (
                <button
                  type="submit"
                  disabled={selectedOrder.state === 20}
                  style={{
                    padding: '5px 12px',
                    backgroundColor: selectedOrder.state === 20 ? '#9ca3af' : '#2563eb',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: selectedOrder.state === 20 ? 'not-allowed' : 'pointer',
                    fontSize: '12px',
                    opacity: selectedOrder.state === 20 ? 0.6 : 1
                  }}
                >
                  주문 정정
                </button>
              )}
              {pfCanOrder && !selectedOrder && (
                <button
                  type="submit"
                  style={{
                    padding: '5px 12px',
                    backgroundColor: '#2563eb',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '12px'
                  }}
                >
                  주문 제출
                </button>
              )}
              <button
                type="button"
                onClick={() => {
                  setShowOrderForm(false);
                  setSelectedOrder(null);
                }}
                style={{
                  padding: '5px 12px',
                  color: '#6b7280',
                  backgroundColor: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '12px'
                }}
              >
                × 닫기
              </button>
            </div>
          </form>
        </div>
        </div>
      )}

      {/* 잔고 섹션 */}
      <div style={{ backgroundColor: 'white', border: '2px solid #3b82f6', borderRadius: '8px', overflow: 'hidden', marginBottom: '20px' }}>
        <div className="ag-theme-quartz" style={{ height: Math.max(80, 50 + recentBalance.length * 32), width: '100%', fontSize: '12px' }}>
          <AgGridReact {...copyMenu.gridProps}
            rowData={recentBalance}
            columnDefs={balanceColumnDefs}
            defaultColDef={defaultColDef}
            overlayNoRowsTemplate="해당 일자 잔고 데이터 없음"
            animateRows={true}
            enableCellTextSelection={true}
            pagination={false}
            headerHeight={36}
            rowHeight={32}
          />
        </div>
      </div>

      {/* 손익(pfbs) 섹션 — 바로 위 KIS 잔고 그리드와 **대조용** (2026-10-08).
          위는 KIS 가 준 값, 아래는 체결로 우리가 계산한 값 + 그 차이.
          같은 조회일자의 한 행끼리 나란히 놓고 본다. */}
      <div style={{ backgroundColor: 'white', border: '2px solid #3b82f6', borderRadius: '8px', padding: '12px 16px 16px', marginBottom: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', flexWrap: 'wrap', gap: 8 }}>
          <h3 style={{ fontSize: '12px', fontWeight: '600', color: '#1e40af' }}>
            손익 집계 <span style={{ fontSize: '11px', fontWeight: '400', color: '#9ca3af' }}>
              (위 KIS 잔고와 대조 — 체결로 자체 계산 · 행 클릭: 종목별 세부)
            </span>
          </h3>
          {pnlRows.length > 0 && (() => {
            // 그날 판정을 한 줄로. **KIS 원본(kisGap)** 기준으로 본다 —
            // navDiff 는 우리가 뺀 값이라 그걸로 비교하면 KIS 가 틀린 날을 못 잡는다.
            const r = pnlRows[0];
            if (r.kisGap == null) {
              return <span style={{ fontSize: '12px', color: '#9ca3af' }}>
                미청산 — 이월분 미실현은 자체계산에 안 잡혀 대조 불가
              </span>;
            }
            const kisOk = Math.abs(r.kisGap) < 1;
            if (kisOk) {
              return <span style={{ fontSize: '12px', fontWeight: 600, color: '#16a34a' }}>
                KIS 원본과 일치 ✓
              </span>;
            }
            // KIS 와 어긋난다. NAV 증감과는 맞는지로 어느 쪽이 틀렸는지 가린다.
            const navOk = r.diffCheck != null && Math.abs(r.diffCheck) < 1;
            return (
              <span style={{ fontSize: '12px', fontWeight: 600, color: '#b45309' }}>
                KIS 원본과 {Math.round(r.kisGap).toLocaleString()} 차이
                <span style={{ fontWeight: 400 }}>
                  {navOk
                    ? ' — 순자산 증감과는 일치하므로 KIS 값이 틀렸다'
                    : ' — 순자산 증감과도 어긋난다. 집계를 확인할 것'}
                </span>
              </span>
            );
          })()}
        </div>
        <div className="ag-theme-quartz" style={{ height: Math.max(80, 50 + pnlRows.length * 32), width: '100%', fontSize: '12px' }}>
          <AgGridReact {...copyMenu.gridProps}
            rowData={pnlRows}
            columnDefs={pnlColumnDefs}
            defaultColDef={defaultColDef}
            overlayNoRowsTemplate="해당 일자 손익 집계 없음 (GenPfBSJob 실행 전이거나 그날 거래 없음)"
            animateRows={false}
            onRowClicked={(e) => {
              if (!e.data) return;
              // 같은 행을 다시 누르면 닫는다
              if (tradeDetailD === e.data.d) { setTradeDetail(null); setTradeDetailD(null); return; }
              fetchTradeDetail(e.data.pfId, e.data.d);
            }}
            getRowStyle={(p) => p.data?.d === tradeDetailD
              ? { backgroundColor: '#dbeafe', fontWeight: 600, cursor: 'pointer' }
              : { cursor: 'pointer' }}
            enableCellTextSelection={true}
            pagination={false}
            headerHeight={36}
            rowHeight={32}
          />
        </div>
      </div>

      {/* 체결손익 세부 — 손익 집계 행을 클릭하면 펼친다.
          위 '체결손익' 이 어느 종목에서 나왔는지 분해해 보여준다. */}
      {tradeDetailD && (
        <div style={{ backgroundColor: 'white', border: '2px solid #8b5cf6', borderRadius: '8px', padding: '12px 16px 16px', marginBottom: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', flexWrap: 'wrap', gap: 8 }}>
            <h3 style={{ fontSize: '12px', fontWeight: '600', color: '#6d28d9' }}>
              체결손익 세부 ({tradeDetailD}) <span style={{ fontSize: '11px', fontWeight: '400', color: '#9ca3af' }}>
                (종목별 매도대금 − 매수대금 · 합계가 위 체결손익이다)
              </span>
            </h3>
            {tradeDetail && tradeDetail.length > 0 && (() => {
              const sum = tradeDetail.reduce((a, r) => a + (r.pnl ?? 0), 0);
              return (
                <span style={{ fontSize: '12px', fontWeight: 600,
                               color: sum >= 0 ? '#16a34a' : '#dc2626' }}>
                  합계 {Math.round(sum).toLocaleString()}원
                  <span style={{ color: '#9ca3af', fontWeight: 400 }}> · {tradeDetail.length}종목 (비용 전)</span>
                </span>
              );
            })()}
          </div>
          <div className="ag-theme-quartz" style={{ height: Math.max(90, 50 + (tradeDetail?.length ?? 0) * 32), width: '100%', fontSize: '12px' }}>
            <AgGridReact {...copyMenu.gridProps}
              rowData={tradeDetail ?? []}
              columnDefs={tradeDetailCols}
              defaultColDef={defaultColDef}
              overlayNoRowsTemplate="그날 체결이 없습니다"
              animateRows={false}
              enableCellTextSelection={true}
              pagination={false}
              headerHeight={36}
              rowHeight={32}
            />
          </div>
        </div>
      )}

      {/* 전략 슬롯 — 고르면 아래 주문·체결·슬라이스가 그 슬롯으로 걸러진다 (2026-10-23) */}
      {slotRows.length > 0 && (
        <div style={{ backgroundColor: 'white', border: '2px solid #8b5cf6', borderRadius: '8px', padding: '16px', marginBottom: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <h3 style={{ fontSize: '12px', fontWeight: '600', color: '#6d28d9' }}>
              전략 슬롯 <span style={{ fontSize: '11px', fontWeight: '400', color: '#9ca3af' }}>(행 클릭: 아래 주문·체결·슬라이스를 그 슬롯만 · 다시 클릭/전체: 해제)</span>
            </h3>
            <span style={{ color: '#6d28d9', fontSize: '12px', fontWeight: 500 }}>
              {slotFilter ? <>선택: <b>{slotFilterNm}</b>{' '}
                <a style={{ color: '#6b7280', cursor: 'pointer' }} onClick={() => selectSlot('')}>[전체 보기]</a></> : `${slotRows.length}개`}
            </span>
          </div>
          <div className="ag-theme-quartz" style={{ width: '100%', fontSize: '12px' }}>
            <AgGridReact {...copyMenu.gridProps}
              rowData={slotRows}
              pinnedBottomRowData={slotTotalRow}
              columnDefs={slotGridCols}
              defaultColDef={{ sortable: false, resizable: true, filter: false }}
              domLayout="autoHeight"
              animateRows={false}
              headerHeight={34}
              rowHeight={30}
              onRowClicked={(e: any) => {
                if (e.rowPinned) { selectSlot(''); return; }
                const k = e.data?.key ?? '';
                selectSlot(slotFilter === k ? '' : k);
              }}
              getRowStyle={(p: any) => p.node.rowPinned
                ? { fontWeight: 600, backgroundColor: '#f8fafc' }
                : p.data?.key === slotFilter ? { backgroundColor: '#ede9fe', fontWeight: 600 }
                : p.data?.kind === '미연결' ? { color: '#9ca3af' } : undefined}
            />
          </div>
        </div>
      )}

      {/* 주문 내역 섹션 */}
      <div style={{ backgroundColor: 'white', border: '2px solid #3b82f6', borderRadius: '8px', padding: '16px', marginBottom: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <h3 style={{ fontSize: '12px', fontWeight: '600', color: '#1e40af' }}>주문 내역 <span style={{ fontSize: '11px', fontWeight: '400', color: '#9ca3af' }}>(우클릭: 정정/취소)</span></h3>
          <div style={{ color: '#1e40af', fontSize: '12px', fontWeight: '500' }}>
            {slotFilter ? <span style={{ color: '#6d28d9' }}>[{slotFilterNm}] </span> : null}
            총 {ordersView.length}건{slotFilter ? ` / ${orders.length}` : ''}
          </div>
        </div>

        <div
          ref={orderGridRef}
          className="ag-theme-quartz"
          style={{ height: 360, width: '100%', fontSize: '12px', position: 'relative' }}
          onContextMenu={(e) => {
            e.preventDefault();
            const target = e.target as HTMLElement;
            const rowElement = target.closest('.ag-row');
            if (rowElement) {
              const rowIndex = parseInt(rowElement.getAttribute('row-index') || '-1');
              if (rowIndex >= 0 && rowIndex < ordersView.length) {
                const order = ordersView[rowIndex];
                const { cellValue, rowText } = getAgGridCellInfo(e);
                setOrderContextMenu({ x: e.clientX, y: e.clientY, order, cellValue, rowText });
              }
            }
          }}
        >
          <AgGridReact
            rowData={ordersView}
            columnDefs={orderColumnDefs}
            defaultColDef={defaultColDef}
            // 툴팁 기본 지연이 2초라 안 뜨는 것처럼 느껴진다. 0.3초로 당긴다.
            tooltipShowDelay={300}
            tooltipHideDelay={60000}
            overlayNoRowsTemplate="주문 내역이 없습니다"
            animateRows={true}
            rowSelection="multiple"
            suppressRowClickSelection={true}
            suppressContextMenu={true}
            enableCellTextSelection={true}
            pagination={true}
            paginationPageSize={500}
            paginationPageSizeSelector={[10, 15, 20, 25, 50, 100, 200, 500, 1000]}
            headerHeight={40}
            rowHeight={32}
            onRowClicked={(event) => {
              const order = event.data as PfOrder;
              if (order.id) {
                setSliceSlotNm(null);
                fetchSlices(order.id);
              }
            }}
          />
          {/* 긴 메시지 전문 보기 — 에러메시지·코멘트 셀을 클릭하면 열린다.
              툴팁만으로는 아주 긴 글이 잘리고 복사도 안 돼서 패널을 따로 둔다. */}
          {/* 분봉 차트 — 주문/체결 그리드의 종목코드를 클릭하면 열린다.
              캔들 위에 ▲매수 / ▼매도 표식을 찍어 "언제 어느 가격에 샀나"를 본다.
              체결=채운 삼각형, 미체결 주문=빈 삼각형. */}
          {msgDetail && (
            <div style={{
              marginTop: 8, padding: '10px 12px',
              border: '1px solid #bfdbfe', borderRadius: 6, backgroundColor: '#eff6ff',
              fontSize: 12, lineHeight: 1.6,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 6 }}>
                <span style={{ fontWeight: 700, color: '#1e3a8a' }}>{msgDetail.title}</span>
                <button
                  onClick={() => { navigator.clipboard?.writeText(msgDetail.text); }}
                  style={{
                    marginLeft: 8, padding: '2px 8px', fontSize: 11, cursor: 'pointer',
                    border: '1px solid #93c5fd', borderRadius: 4, backgroundColor: '#fff', color: '#1d4ed8',
                  }}
                >복사</button>
                <button
                  onClick={() => setMsgDetail(null)}
                  style={{
                    marginLeft: 'auto', padding: '2px 8px', fontSize: 11, cursor: 'pointer',
                    border: '1px solid #cbd5e1', borderRadius: 4, backgroundColor: '#fff', color: '#475569',
                  }}
                >닫기</button>
              </div>
              {/* pre-wrap: 줄바꿈 유지 + 자동 줄바꿈. 길어지면 스크롤 */}
              <div style={{
                whiteSpace: 'pre-wrap', wordBreak: 'break-all',
                maxHeight: 160, overflowY: 'auto',
                fontFamily: 'Consolas, monospace', color: '#1f2937',
                userSelect: 'text',
              }}>{msgDetail.text}</div>
            </div>
          )}
          {orderContextMenu && (
            <>
              <div
                style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1000 }}
                onClick={() => setOrderContextMenu(null)}
              />
              <div
                style={{
                  position: 'fixed',
                  top: orderContextMenu.y,
                  left: orderContextMenu.x,
                  backgroundColor: 'white',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                  zIndex: 1001,
                  minWidth: '190px',
                  padding: '4px 0',
                }}
                onClick={(e) => e.stopPropagation()}
              >
                {(() => {
                  const o = orderContextMenu.order;
                  const canModify = o.state >= 0 && o.state < 30;
                  const canCancel = o.state >= 0 && o.state < 30;
                  // 우클릭한 종목의 보유수량 — 있으면 매도 주문의 기본 수량으로 쓴다
                  const heldQty = positions.find(
                    p => (p.prodid || p.prodId) === o.prodId)?.pos ?? 0;
                  return (
                    <>
                      {pfCanOrder && (<>
                      {/* ── 신규 주문 ─────────────────────────── */}
                      <div style={menuLabelStyle}>신규 주문</div>
                      <div
                        style={{
                          padding: '5px 12px', cursor: 'pointer',
                          color: '#059669', fontSize: '14px', fontWeight: 600,
                        }}
                        onClick={() => {
                          if (portfolioId === 0) {
                            alert('주문은 개별 계좌를 선택해야 합니다.');
                            setOrderContextMenu(null); return;
                          }
                          setSelectedOrder(null);
                          setFormData({
                            date: selectedDate,
                            prodId: o.prodId || '',
                            prodNm: o.prodNm || '',
                            isBuy: false,              // 보유종목이므로 매도가 기본
                            wei: 0,
                            qty: heldQty > 0 ? heldQty : 1,
                            msg: '',
                            exitAll: false,
                            prcTy: 'Opp',
                            oppLmtIdx: 1,
                            myLmtIdx: 1,
                            limitPrc: null,
                            stopPrc: null,
                            isSlice: false,
                            hm0: getSliceStartTime(selectedDate),
                            hm1: '15:20',
                            nSlices: null,
                          });
                          setErrors({});
                          setStockSearchTerm('');
                          setStockSearchResults([]);
                          setShowStockSearch(false);
                          setShowOrderForm(true);
                          setOrderContextMenu(null);
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#ecfdf5'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'white'; }}
                      >
                        매도 주문 {o.prodNm ? `· ${o.prodNm}` : ''}
                        {heldQty > 0 && (
                          <span style={{ color: '#6b7280', fontSize: '12px' }}> ({heldQty}주 보유)</span>
                        )}
                      </div>
                      <div
                        style={{
                          padding: '5px 12px', cursor: 'pointer',
                          color: '#1d4ed8', fontSize: '14px', fontWeight: 600,
                        }}
                        onClick={() => {
                          if (portfolioId === 0) {
                            alert('주문은 개별 계좌를 선택해야 합니다.');
                            setOrderContextMenu(null); return;
                          }
                          setSelectedOrder(null);
                          setFormData({
                            date: selectedDate,
                            prodId: '', prodNm: '',
                            isBuy: true, wei: 0, qty: null, msg: '',
                            exitAll: false, prcTy: 'Opp', oppLmtIdx: 1, myLmtIdx: 1,
                            limitPrc: null, stopPrc: null, isSlice: false,
                            hm0: getSliceStartTime(selectedDate), hm1: '15:20', nSlices: null,
                          });
                          setErrors({});
                          setStockSearchTerm('');
                          setStockSearchResults([]);
                          setShowStockSearch(false);
                          setShowOrderForm(true);
                          setOrderContextMenu(null);
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#eff6ff'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'white'; }}
                      >
                        신규 주문 (종목 선택)
                      </div>

                      {/* ── 이 주문 처리 ──────────────────────── */}
                      <div style={menuSepStyle} />
                      <div style={menuLabelStyle}>주문 {o.id}</div>
                      <div
                        style={{
                          padding: '5px 12px',
                          cursor: canModify ? 'pointer' : 'not-allowed',
                          color: canModify ? '#333' : '#ccc',
                          fontSize: '14px',
                        }}
                        onClick={() => {
                          if (canModify) {
                            setSelectedOrder(o);
                            setShowOrderForm(true);
                          }
                          setOrderContextMenu(null);
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f0f0f0'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'white'; }}
                      >
                        주문 정정
                      </div>
                      <div
                        style={{
                          padding: '5px 12px',
                          cursor: canCancel ? 'pointer' : 'not-allowed',
                          color: canCancel ? '#ef4444' : '#ccc',
                          fontSize: '14px',
                          fontWeight: '500',
                        }}
                        onClick={() => {
                          if (canCancel) {
                            handleCancel(o);
                          }
                          setOrderContextMenu(null);
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f0f0f0'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'white'; }}
                      >
                        주문 취소
                      </div>
                      {o.state === 0 && (
                        <div
                          style={{
                            padding: '5px 12px',
                            cursor: 'pointer',
                            color: '#dc2626',
                            fontSize: '14px',
                            fontWeight: '600',
                            borderTop: '1px solid #e2e8f0',
                          }}
                          onClick={async () => {
                            if (!confirm(`주문 ${o.id}을 삭제하시겠습니까?`)) { setOrderContextMenu(null); return; }
                            try {
                              await axiosInstance.delete(`/api/v1/orders/${o.id}`);
                              alert('주문이 삭제되었습니다.');
                              if (selectedPfId) fetchOrders(selectedPfId, selectedDate, selectedDate);
                            } catch (err: any) {
                              alert('삭제 실패: ' + (err.response?.data?.message || err.message));
                            }
                            setOrderContextMenu(null);
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#fef2f2'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'white'; }}
                        >
                          주문 삭제
                        </div>
                      )}
                      </>)}
                                            {/* ── 복사 ─────────────────────────────── */}
                      <div style={menuSepStyle} />
                      <div style={menuLabelStyle}>
                        복사 · 주문내역 {orders.length.toLocaleString()}행
                      </div>
                      <div
                        style={{ padding: '5px 12px', cursor: 'pointer', color: '#6b7280', fontSize: '13px' }}
                        onClick={() => {
                          copyToClipboard(orderContextMenu.cellValue || '');
                          setOrderContextMenu(null);
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f0f0f0'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'white'; }}
                      >
                        셀 복사
                      </div>
                      <div
                        style={{ padding: '5px 12px', cursor: 'pointer', color: '#6b7280', fontSize: '13px' }}
                        onClick={() => {
                          copyToClipboard(orderContextMenu.rowText || '');
                          setOrderContextMenu(null);
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f0f0f0'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'white'; }}
                      >
                        행 복사
                      </div>
                      <div style={{ borderTop: '1px solid #e2e8f0', margin: '4px 0' }} />
                      <div
                        style={{ padding: '5px 12px', cursor: 'pointer', color: '#374151', fontSize: '13px', fontWeight: 600 }}
                        onClick={() => {
                          copyToClipboard(serializeOrders(true));
                          setOrderContextMenu(null);
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f0f0f0'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'white'; }}
                      >
                        전체 복사 <span style={{ color: '#9ca3af', fontSize: '11px' }}>헤더 포함</span>
                      </div>
                      <div
                        style={{ padding: '5px 12px', cursor: 'pointer', color: '#6b7280', fontSize: '13px' }}
                        onClick={() => {
                          copyToClipboard(serializeOrders(false));
                          setOrderContextMenu(null);
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f0f0f0'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'white'; }}
                      >
                        전체 복사 <span style={{ color: '#9ca3af', fontSize: '11px' }}>값만</span>
                      </div>
                    </>
                  );
                })()}
              </div>
            </>
          )}
        </div>
        {chartStock && (
          <div style={{
            marginTop: 10, padding: '10px 12px',
            border: '1px solid #bfdbfe', borderRadius: 6, backgroundColor: '#fff',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
              <span style={{ fontWeight: 700, color: '#1e3a8a', fontSize: 13 }}>
                {chartStock.prodNm} ({chartStock.prodId})
              </span>
              <span style={{ marginLeft: 10, fontSize: 11, color: '#64748b' }}>
                ▲매수 ▼매도 · 채움=체결, 빈삼각형=미체결주문 · 표식 {chartMarkerStat.drawn}건
                {chartMarkerStat.skipped > 0 &&
                  ` (가격없는 주문 ${chartMarkerStat.skipped}건은 표시 못함 — 거절/시장가)`}
              </span>
              {/* 닫기 대신 종목 선택 — 차트는 항상 떠 있어야 한다 (2026-10-03).
                  닫아버리면 자동선택이 다시 띄워 버튼이 동작 안 하는 것처럼 보인다. */}
              <select
                value={chartStock.prodId}
                onChange={(e) => {
                  const id = e.target.value;
                  const nm = chartStockOptions.find(o => o.prodId === id)?.prodNm || id;
                  setChartStock({ prodId: id, prodNm: nm });
                  setUserPickedStock(true);
                }}
                style={{
                  marginLeft: 'auto', padding: '2px 8px', fontSize: 11, cursor: 'pointer',
                  border: '1px solid #93c5fd', borderRadius: 4, backgroundColor: '#fff', color: '#1d4ed8',
                }}
              >
                {chartStockOptions.map(o => (
                  <option key={o.prodId} value={o.prodId}>{o.prodNm} ({o.prodId})</option>
                ))}
              </select>
            </div>
            <StockChart
              // key 로 종목이 바뀌면 컴포넌트를 새로 만든다.
              // 없으면 React 가 인스턴스를 재사용해 내부 period·데이터가 남고,
              // 표식만 갈아끼워져 "클릭해도 안 바뀐다" 로 보인다 (2026-10-04 보고).
              key={chartStock.prodId}
              prodId={chartStock.prodId}
              prodNm={chartStock.prodNm}
              // 조회일자 하루만 그린다 — 여러 날이면 x축에 09:00~15:00 이
              // 날짜 수만큼 반복돼 같은 시각이 중복돼 보인다.
              date={selectedDate}
              height={520}
              markers={chartMarkers}
            />
          </div>
        )}
      </div>

      {/* 슬라이스 상세 섹션 */}
      {(sliceOrderId !== null || sliceSlotNm !== null) && (
        <div style={{ backgroundColor: 'white', border: '2px solid #8b5cf6', borderRadius: '8px', padding: '16px', marginBottom: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <h3 style={{ fontSize: '12px', fontWeight: '600', color: '#6d28d9' }}>슬라이스 상세 ({sliceOrderId !== null ? `주문 ID: ${sliceOrderId}` : `슬롯: ${sliceSlotNm}`})</h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ color: '#6d28d9', fontSize: '12px', fontWeight: '500' }}>총 {slices.length}건</span>
              <button
                onClick={() => { setSliceOrderId(null); setSliceSlotNm(null); setSlices([]); }}
                style={{ color: '#6b7280', fontSize: '16px', background: 'none', border: 'none', cursor: 'pointer', padding: '0 4px' }}
                title="닫기"
              >
                ×
              </button>
            </div>
          </div>
          <div className="ag-theme-quartz" style={{ height: 360, width: '100%', fontSize: '12px' }}>
            <AgGridReact {...copyMenu.gridProps}
              rowData={slices}
              columnDefs={sliceColumnDefs}
              overlayNoRowsTemplate="슬라이스가 없습니다"
              defaultColDef={defaultColDef}
              animateRows={true}
              enableCellTextSelection={true}
              headerHeight={34}
              rowHeight={30}
            />
          </div>
        </div>
      )}

      {/* 체결 내역 섹션 */}
      <div style={{ backgroundColor: 'white', border: '2px solid #3b82f6', borderRadius: '8px', padding: '16px', marginBottom: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <h3 style={{ fontSize: '12px', fontWeight: '600', color: '#1e40af' }}>
              체결 내역{fillsFiltered && sliceOrderId ? ` (주문 #${sliceOrderId})` : ''}
            </h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ color: '#1e40af', fontSize: '12px', fontWeight: '500' }}>
              {slotFilter ? <span style={{ color: '#6d28d9' }}>[{slotFilterNm}] </span> : null}
              총 {fillsView.length}건
            </span>
            <button
              onClick={() => fetchFills(apiPfId, selectedDate, selectedDate, true)}
              style={{
                padding: '3px 10px', backgroundColor: '#3b82f6', color: 'white', border: 'none', borderRadius: '4px',
                fontSize: '11px', fontWeight: '500', cursor: 'pointer',
              }}
            >전체조회</button>
          </div>
        </div>

        <div 
          ref={fillGridRef}
          className="ag-theme-quartz"
          style={{ height: 360, width: '100%', fontSize: '12px', position: 'relative' }}
          onContextMenu={(e) => {
            e.preventDefault();
            const target = e.target as HTMLElement;
            const rowElement = target.closest('.ag-row');
            if (rowElement) {
              const rowIndex = parseInt(rowElement.getAttribute('row-index') || '-1');
              const displayFills = fillsView;
              if (rowIndex >= 0 && rowIndex < displayFills.length) {
                const fill = displayFills[rowIndex];
                const { cellValue, rowText } = getAgGridCellInfo(e);
                setContextMenu({ x: e.clientX, y: e.clientY, fill, cellValue, rowText });
              }
            }
          }}
        >
          <AgGridReact
            rowData={fillsView}
            columnDefs={fillColumnDefs}
            defaultColDef={defaultColDef}
            overlayNoRowsTemplate="체결 내역이 없습니다"
            animateRows={true}
            rowSelection="multiple"
            suppressRowClickSelection={true}
            enableCellTextSelection={true}
            pagination={true}
            paginationPageSize={500}
            paginationPageSizeSelector={[10, 15, 20, 25, 50, 100, 200, 500, 1000]}
            suppressContextMenu={true}
            headerHeight={40}
            rowHeight={32}
          />
          {contextMenu && (
            <>
              <div
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: 1000,
                }}
                onClick={() => setContextMenu(null)}
              />
              <div
                style={{
                  position: 'fixed',
                  top: contextMenu.y,
                  left: contextMenu.x,
                  backgroundColor: 'white',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                  zIndex: 1001,
                  minWidth: '150px',
                  padding: '4px 0',
                }}
                onClick={(e) => e.stopPropagation()}
              >
                {(() => {
                  const fill = contextMenu.fill;
                  // 테스트를 위해 항상 활성화, 실제로는: const canCancel = fill.rmnQty !== null && fill.rmnQty !== undefined && fill.rmnQty !== 0 && fill.cnclYn !== 'Y';
                  const canCancel = true; // 테스트용: 항상 활성화
                  
                  return (
                    <>
                      <div
                        style={{
                          padding: '5px 12px',
                          cursor: 'pointer',
                          backgroundColor: 'white',
                          color: '#1e40af',
                          fontSize: '14px',
                          fontWeight: 600,
                        }}
                        onClick={() => {
                          // ordT 는 "yyyy-MM-ddTHH:mm:ss" 형태 → HHmmss 로 바꾼다
                          const t = String(fill.ordT ?? '');
                          const hhmmss = t.includes('T')
                            ? t.split('T')[1].slice(0, 8).replace(/:/g, '')
                            : t.slice(-8).replace(/:/g, '');
                          setMarketAt({
                            prodId: fill.prodId,
                            prodNm: fill.prodNm,
                            d: String(fill.d ?? '').slice(0, 10),
                            tm: hhmmss,
                            fillPrc: fill.avgPrc ?? fill.ordPrc ?? undefined,
                          });
                          setContextMenu(null);
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#eff6ff'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'white'; }}
                      >
                        호가·체결 확인
                      </div>
                      {/* 체결 취소·정정 — 계좌를 받지 않고 KIS 를 직접 치는 API 라 Dev 만 (2026-10-27) */}
                      {isDev && (<>
                      <div style={{ borderTop: '1px solid #e5e7eb', margin: '2px 0' }} />
                      <div
                        style={{
                          padding: '5px 12px',
                          cursor: 'pointer',
                          backgroundColor: 'white',
                          color: '#333',
                          fontSize: '14px',
                        }}
                        onClick={() => {
                          handleCancelOrder(fill);
                          setContextMenu(null);
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#f0f0f0';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'white';
                        }}
                      >
                        주문 취소
                      </div>
                      <div
                        style={{
                          padding: '5px 12px',
                          cursor: 'pointer',
                          backgroundColor: 'white',
                          color: '#333',
                          fontSize: '14px',
                        }}
                        onClick={() => {
                          handleModifyOrder(fill);
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#f0f0f0';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'white';
                        }}
                      >
                        주문 정정
                      </div>
                      </>)}
                      <div style={{ borderTop: '1px solid #e5e7eb', margin: '2px 0' }} />
                      <div
                        style={{ padding: '5px 12px', cursor: 'pointer', color: '#6b7280', fontSize: '13px' }}
                        onClick={() => {
                          copyToClipboard(contextMenu.cellValue || '');
                          setContextMenu(null);
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f0f0f0'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'white'; }}
                      >
                        셀 복사
                      </div>
                      <div
                        style={{ padding: '5px 12px', cursor: 'pointer', color: '#6b7280', fontSize: '13px' }}
                        onClick={() => {
                          copyToClipboard(contextMenu.rowText || '');
                          setContextMenu(null);
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f0f0f0'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'white'; }}
                      >
                        행 복사
                      </div>
                    </>
                  );
                })()}
              </div>
            </>
          )}
        </div>
      </div>

      {/* 포지션 현황 섹션 */}
      <div style={{ backgroundColor: 'white', border: '2px solid #3b82f6', borderRadius: '8px', padding: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <h3 style={{ fontSize: '12px', fontWeight: '600', color: '#1e40af' }}>포지션 현황</h3>
            <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
              <span style={{ fontSize: '11px', color: '#6b7280' }}>
                {positionSummary.positionCount}종목
              </span>
              <span style={{ fontSize: '11px', color: '#6b7280' }}>
                매입 <b style={{ color: '#1e3a8a' }}>{positionSummary.totalBamt.toLocaleString()}</b>
              </span>
              <span style={{ fontSize: '11px', color: '#6b7280' }}>
                평가 <b style={{ color: '#1e3a8a' }}>{positionSummary.totalValue.toLocaleString()}</b>
              </span>
              <span style={{ fontSize: '11px', color: '#6b7280' }}>
                손익 <b style={{ color: positionSummary.totalPrft >= 0 ? '#16a34a' : '#dc2626' }}>{positionSummary.totalPrft.toLocaleString()}</b>
              </span>
              <span style={{ fontSize: '11px', color: '#6b7280' }}>
                수익률 <b style={{ color: positionSummary.totalPrft >= 0 ? '#16a34a' : '#dc2626' }}>{positionSummary.totalWei.toFixed(1)}%</b>
              </span>
            </div>
          </div>

        {/* 포지션 그리드 */}
        <div className="ag-theme-quartz" style={{ height: 360, width: '100%', fontSize: '12px', position: 'relative' }}
          onContextMenu={(e) => {
            e.preventDefault();
            const target = e.target as HTMLElement;
            const rowElement = target.closest('.ag-row');
            if (rowElement) {
              const rowIndex = parseInt(rowElement.getAttribute('row-index') || '-1');
              // 포지션 우클릭 메뉴는 매수·매도 주문뿐이라 주문 권한이 없으면 열지 않는다
              if (pfCanOrder && rowIndex >= 0 && rowIndex < positions.length) {
                setPosContextMenu({ x: e.clientX, y: e.clientY, pos: positions[rowIndex] });
              }
            }
          }}
        >
          <AgGridReact
            rowData={positions}
            columnDefs={positionColumnDefs}
            defaultColDef={defaultColDef}
            overlayNoRowsTemplate="보유 종목이 없습니다"
            animateRows={true}
            rowSelection="multiple"
            suppressRowClickSelection={true}
            enableCellTextSelection={true}
            pagination={true}
            paginationPageSize={500}
            paginationPageSizeSelector={[10, 15, 20, 25, 50, 100, 200, 500, 1000]}
            suppressContextMenu={true}
          />
          {posContextMenu && (
            <>
              <div
                style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1000 }}
                onClick={() => setPosContextMenu(null)}
              />
              <div
                style={{
                  position: 'fixed', top: posContextMenu.y, left: posContextMenu.x,
                  backgroundColor: 'white', border: '1px solid #ccc', borderRadius: '4px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)', zIndex: 1001, minWidth: '120px', padding: '4px 0',
                }}
              >
                <div style={{ padding: '4px 12px', fontSize: '11px', color: '#999', borderBottom: '1px solid #eee' }}>
                  {posContextMenu.pos.prodNm || posContextMenu.pos.prodid} ({posContextMenu.pos.pos}주)
                  {(posContextMenu.pos as any).ordpsblqty != null && (
                    <span style={{ marginLeft: '4px' }}>
                      · 매도가능 {Number((posContextMenu.pos as any).ordpsblqty).toLocaleString()}
                    </span>
                  )}
                </div>
                <div
                  style={{ padding: '5px 12px', cursor: 'pointer', fontSize: '14px', color: '#ef4444' }}
                  onClick={() => openOrderFromPos(posContextMenu.pos, true)}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f0f0f0'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'white'; }}
                >
                  매수
                </div>
                {(() => {
                  // 매도가능수량이 0 이면 이미 청산 주문이 걸려 있다 —
                  // 여기서 또 내면 KIS 가 "전량 청산 주문 작동 시 추가 청산 불가"로 거절한다.
                  // 실제로 2026-09-15 에 그 사실을 화면에서 알 수 없어 재주문이 반복됐다.
                  const held = posContextMenu.pos.pos ?? 0;
                  const sellable = (posContextMenu.pos as any).ordpsblqty;
                  const blocked = held > 0 && sellable === 0;

                  return blocked ? (
                    <div
                      style={{
                        padding: '5px 12px', fontSize: '13px',
                        color: '#9ca3af', cursor: 'not-allowed', backgroundColor: '#f9fafb',
                      }}
                      title="이미 청산 주문이 걸려 있다 (미체결)"
                    >
                      매도 <span style={{ fontSize: '11px' }}>— 주문 진행 중</span>
                    </div>
                  ) : (
                    <div
                      style={{ padding: '5px 12px', cursor: 'pointer', fontSize: '14px', color: '#22c55e' }}
                      onClick={() => openOrderFromPos(posContextMenu.pos, false)}
                      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f0f0f0'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'white'; }}
                    >
                      매도
                    </div>
                  );
                })()}
              </div>
            </>
          )}
        </div>
      </div>

      {/* 주문 정정 모달 */}
      {showModifyModal && modifyFill && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000,
          }}
          onClick={() => {
            setShowModifyModal(false);
            setModifyFill(null);
          }}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              padding: '20px',
              minWidth: '350px',
              maxWidth: '450px',
              width: '90%',
              maxHeight: '90vh',
              overflow: 'auto',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              boxSizing: 'border-box',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 20px 0', color: '#1e40af', fontSize: '12px' }}>
              주문 정정
            </h3>
            
            <div style={{ marginBottom: '16px' }}>
              <div style={{ marginBottom: '8px', fontSize: '14px', color: '#666' }}>
                종목: <strong>{modifyFill.prodNm}</strong> ({modifyFill.prodId})
              </div>
              <div style={{ marginBottom: '8px', fontSize: '14px', color: '#666' }}>
                주문번호: <strong>{modifyFill.ordNo}</strong>
              </div>
              <div style={{ marginBottom: '8px', fontSize: '14px', color: '#666' }}>
                현재 가격: <strong>{modifyFill.ordPrc?.toLocaleString()}</strong>원
              </div>
              <div style={{ marginBottom: '8px', fontSize: '14px', color: '#666' }}>
                잔여수량: <strong>{(modifyFill.rmnQty || modifyFill.ordQty)?.toLocaleString()}</strong>주
              </div>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', color: '#333', fontWeight: '500' }}>
                정정 가격 (원)
              </label>
              <input
                type="number"
                value={modifyPrice}
                onChange={(e) => setModifyPrice(e.target.value)}
                placeholder="가격을 입력하세요"
                style={{
                  width: '100%',
                  padding: '6px 10px',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  fontSize: '14px',
                  boxSizing: 'border-box',
                }}
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', color: '#333', fontWeight: '500' }}>
                정정 수량 (주)
              </label>
              <input
                type="number"
                value={modifyQty}
                onChange={(e) => setModifyQty(e.target.value)}
                placeholder="수량을 입력하세요"
                min="1"
                max={modifyFill.rmnQty || modifyFill.ordQty}
                style={{
                  width: '100%',
                  padding: '6px 10px',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  fontSize: '14px',
                  boxSizing: 'border-box',
                }}
              />
              <div style={{ marginTop: '4px', fontSize: '12px', color: '#666' }}>
                최대 수량: {(modifyFill.rmnQty || modifyFill.ordQty)?.toLocaleString()}주
              </div>
            </div>

            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowModifyModal(false);
                  setModifyFill(null);
                }}
                style={{
                  padding: '5px 12px',
                  backgroundColor: '#e5e7eb',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                }}
              >
                취소
              </button>
              <button
                onClick={executeModifyOrder}
                style={{
                  padding: '5px 12px',
                  backgroundColor: '#2563eb',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                }}
              >
                정정
              </button>
            </div>
          </div>
        </div>
      )}
      {copyMenu.menu}

      {/* 체결 시점 시장상황 — 체결내역 우클릭 > 호가·체결 확인 */}
      {marketAt && (
        <MarketAtDialog
          prodId={marketAt.prodId}
          prodNm={marketAt.prodNm}
          d={marketAt.d}
          tm={marketAt.tm}
          fillPrc={marketAt.fillPrc}
          onClose={() => setMarketAt(null)}
        />
      )}
    </div>
  );
};

export default PortfolioOverview;

